import moment from "moment";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import ReactDOMServer from "react-dom/server";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Tooltip } from "react-tooltip";
import { xml2js } from "../../json/xml2js";
import {
  cloneForm,
  compareForms,
  deleteFormSubmission,
  getCareTeams,
  getClientData,
  getConfig,
  getFormsySubmissionsForClient,
  getSubmission,
  handleFormChange,
  saveForm,
  sendShareEmail,
  validateForm,
} from "../../scripts/formScripts";
import {
  ArrayCheck,
  getFieldDate,
  getFieldLabel,
  getFieldName,
  getFieldOptions,
  getFieldValue,
} from "../../scripts/helperScripts";
import BackIcon from "../common/BackIcon";
import ClientFormHeader from "../common/ClientFormHeader";
import Comment from "../common/Comment";
import {
  FormCheckbox,
  FormInput,
  FormRadioButton,
  FormSelect,
} from "../common/ControlBundle";
import FormButtonBar from "../common/FormButtonBar";
import ShareModal from "../common/ShareModal";
import Signature from "../common/Signature";
import { getSchema } from "./joi/validation";

const FunctionalAreaExpandDiv = ({
  children,
  title,
  subTitle,
  expandStatus,
  preserveChildren,
  score,
  previousScore,
  ...rest
}) => {
  const [expanded, setExpanded] = useState(expandStatus);

  useEffect(() => {
    setExpanded(expandStatus);
  }, [expandStatus]);

  const scoreIcon = useMemo(() => {
    if (score === undefined || previousScore === undefined) {
      return;
    }

    const icon =
      score > previousScore
        ? "fas fa-lg fa-circle-check text-success"
        : score < previousScore
        ? "fas fa-lg fa-circle-xmark text-danger"
        : score === 2
        ? "fas fa-lg fa-circle-check text-success"
        : "fas fa-lg fa-circle-minus text-warning";
    return <i className={icon} />;
  }, [score, previousScore]);

  return (
    <div {...rest}>
      <div className="row">
        <div className="col-6">
          <span className="h4">
            <i
              title={expanded ? "Collapse" : "Expand"}
              className={`p-1 text-primary ${
                expanded ? "fas fa-chevron-up" : "fas fa-chevron-down"
              }`}
              onClick={() => setExpanded(!expanded)}
            ></i>
            {title}
          </span>
        </div>
        {previousScore ? (
          <>
            <div className="col text-center">
              <div className="fw-bold text-white">Previous Score:</div>
              <div className="bg-light py-2">{previousScore}</div>
            </div>
          </>
        ) : null}
        {score ? (
          <>
            <div className="col text-center">
              <div className="fw-bold text-white">Score:</div>
              <div className="bg-light py-2">{score}</div>
            </div>
          </>
        ) : null}
        {previousScore ? (
          <>
            <div className="col-2 text-center">{scoreIcon}</div>
          </>
        ) : null}
      </div>
      {preserveChildren ? (
        <span className={`${!expanded ? "d-none" : ""}`}>
          <div className="mt-3 bg-light p-2">
            {subTitle}
            <div className="row text-center">
              <div className="col">
                Not Applicable
                <br />
                N/A
              </div>
              <div className="col">
                Clear Strength
                <br />
                +2
              </div>
              <div className="col">
                Mild Strength
                <br />
                +1
              </div>
              <div className="col">
                Baseline/Adequate
                <br />0
              </div>
              <div className="col">
                Mild Problem
                <br />
                -1
              </div>
              <div className="col">
                Moderate Problem
                <br />
                -2
              </div>
              <div className="col">
                Serious Problem
                <br />
                -3
              </div>
              <div className="col">
                Unknown
                <br />
                UK
              </div>
            </div>
          </div>
          {children}
        </span>
      ) : expanded ? (
        children
      ) : null}
    </div>
  );
};

const definitions = {
  AENVStability: {
    strength:
      "Refers to family occupying the same, adequate residence for more than three years. If less than three years, move is prompted by a job change or move to better housing, etc. Rent/mortgage is paid on time. There are no issues meeting financial obligations of rent or mortgage.",
    baseline:
      "Refers to family experiencing, or previously experiencing, minor issues with remaining in the same residence, but family is relatively capable of meeting financial obligations, present housing is not threatened, and family members are not inhibited in pursuing or meeting other obligations due to these minor issues.",
    challenge:
      "Refers to family being threatened with eviction. Unable to meet rent or mortgage obligations on time, or at all. Or, family does not have housing, is living with different relatives or friends, or living in a homeless shelter or shelter for victims of domestic violence. Family is not satisfied with living situation.",
  },
  AENVSafety: {
    strength:
      "Refers to a safe and secure neighborhood for the children. Parents can allow children to play outside without fear. Neighbors look out for each other (neighborhood watch).",
    baseline:
      "Refers to minor disturbances in the neighborhood, but disturbances do not prevent family members and children from spending time outside in the community.",
    challenge:
      "Refers to many disturbances such as fights and/or outbursts in the neighborhood. The neighborhood is not safe for children to play outdoors or walk to the bus or to school. Evidence of violence, boarded up or barred windows, gun fire, the use of alcohol or drugs, and/or drug trafficking in the neighborhood. Neighbors fearful of getting involved.",
  },
  AENVRisks: {
    strength:
      "Refers to family’s living and neighborhood circumstances being essentially free from environmental risks such as toxic chemicals, industrial gases or waste, lead paint or plumbing, vermin infestations, mold, high traffic density, building construction or demolition nearby, or similar risks. Caregivers recognize environmental risks and pursue remedies to protect children and family.",
    baseline:
      "Refers to family’s living and neighborhood circumstances being largely free of known environmental risks. Some environmental risks may be present, but caregivers seem to recognize those risks and are taking appropriate steps to protect children and family, such as lead paint removal, vermin control, and appropriate inspections for mold. Care-givers may not be aware of all potential risks, but generally take appropriate steps to reduce risks.",
    challenge:
      "Family living or neighborhood circumstances are fraught with environmental risks. Rats, roaches, and other vermin present, and droppings are present. Children or family members may suffer asthma due to industrial gases or waste. Abandoned, condemned buildings, or building under construction pose risk to children at play. Children’s play areas not sufficiently protected from traffic or other risks. Lead paint or plumbing pose heavy metal ingestion risk. Caregivers unwilling or incapable of detecting or recognizing risks and do not take appropriate steps to remedy or protect children and family.",
  },
  AENVHabitability: {
    strength:
      "Refers to family and neighbors experiencing home as warm. Home is very clean and neat. Plenty of space and privacy for children. Plenty of furnishings in good repair. Safety precautions are considered and taken, such as the use of smoke alarms and dead bolts on outside doors. Poisonous items are kept locked and out of children’s reach. Plumbing is in good condition.",
    baseline:
      "Refers to minimal issues in the home, such as slight overcrowding, or some clutter. However, most safety precautions are taken (e.g., poisons are out of sight but not locked). Minor house repairs (e.g., crumbling plaster) may be evident, but do not require immediate attention.",
    challenge:
      "Refers to unsanitary situations, including roaches, litter, clutter, and/or unpleasant odors present in the home. Food particles and/or rotting food on the counters and tables. Urine-soaked or stained furniture, dirty diapers, dirty dishes, overflowing garbage, and/or animal or human feces on the floor. Hesitance to sit down or enter the home. Nonfunctioning plumbing and/or no electricity. Many hazards within the reach of children, such as guns, knives, street drugs, or open medication and poisons.",
  },
  AENVLearning: {
    strength:
      "Refers to caregivers’ enthusiasm in teaching their children and supporting education and school attendance. Age-appropriate games, toys and other materials supporting educational pursuits are provided, and evident in the home.",
    baseline:
      "Caregivers occasionally plan time for learning activities in the home. Some age-appropriate, educationally related games/toys/materials are present. Caregivers may not have sufficient resources to provide an abundance of these items, but their desire to provide them and some activities are evident.",
    challenge:
      "Caregivers make no effort to provide opportunities for learning at home. Games/toys/other materials are absent, and/or are not age appropriate or are violent. No identified space for learning activities.",
  },
  AENVOverall: {
    strength:
      "Refers to family receiving very high ratings in the following areas: housing stability, safety in the community, environmental risks, housing habitability, and learning environment.",
    baseline:
      "Refers to family experiencing a few minimal issues in the following areas: housing stability, safety in the community, environmental risks, housing habitability, and learning environment. However, issues do not interfere in family’s ability to function, and issues do not need to be addressed. Some minimal challenges may be due to or made worse by poverty-related issues rather than skill or attitude of caregivers.",
    challenge:
      "Refers to family receiving very low ratings in the following areas: housing stability, safety in the community, environmental risks, housing habitability, and learning environment.",
  },
  BPCSupervision: {
    strength:
      "Refers to caregivers’ provision of age-appropriate supervision, such as setting limits for activities based on the children’s ages. Caregivers are careful and attentive to children’s needs in selecting substitute caregivers (babysitter, neighbor). Makes sure children feel comfortable and safe with substitute caregivers. Keeps track of children and knows children’s friends. Infants and toddlers are under constant, consistent care and supervision in conditions and in surroundings that put the child at ease.",
    baseline:
      "Refers to caregivers providing satisfactory supervision of children. Some limits are set on activities based on the children’s ages. Some consideration given to selecting substitute caregivers, and some concern with children’s comfort with the substitute caregivers. Caregivers have a basic knowledge of location of children and children’s friends. Infants and toddlers are under constant and adequate supervision, assuring that the child is safe, but circumstances may require a variety of caregivers, settings and surroundings.",
    challenge:
      "Refers to caregivers’ lack of age-appropriate supervision, or any supervision. Limits on activities of children are not set or set inconsistently. Little or no consideration given to selecting substitute caregivers (strangers, known abusers, persons under the influence of drugs/alcohol). No thought about children’s comfort and feeling of security with substitute caregivers. Children’s friends are not known, and location of children is not regularly known. Infants and toddlers are sometimes left unattended, or supervision is provided by other children incapable of attending to child’s needs or assuring safety, or child is left in the care of incompetent or uncaring adults or in surroundings that pose risk to child safety or induce serious discomfort.",
  },
  BPCDisciplinaries: {
    strength:
      "Refers to caregivers’ ability to provide age-appropriate, non-punitive, consistent discipline. Uses positive reinforcement and tries to educate children through appropriate discipline. Presents good role model. Caregivers agree on parenting style and support one another.",
    baseline:
      "Refers to caregivers’ adequate provision ofdiscipline andguidance ofchildren.Occasionally discipline is inappropriate to age, too harsh or too lenient, but inconsistencies do not create major issues between children and caregivers.",
    challenge:
      "Refers to caregivers’lack ofdiscipline, or past or current emotional or physical abuse referred to as discipline. Discipline is excessive, punitive, inappropriate to age, inconsistent, and/or absent. Present poor role models. Caregivers disagree on parenting strategies and present mixed messages to child.",
  },
  BPCOpportunities: {
    strength:
      "Refers to caregivers’ encouragement of opportunities such as sports, music lessons, and/or visits to museums and parks. Caregivers do not push children to be involved. Caregivers are actively involved, providing transportation, coaching teams, and/or participating in advisory boards.",
    baseline:
      "Refers to caregivers’ support of opportunities for children such as sports, music lessons, and/or field trips, but caregivers are not actively involved or are involved sporadically in supporting these activities. Limited opportunities may be more related to poverty than skill or attitude of caregivers.",
    challenge:
      "Refers to caregivers’ lack of support or over-involvement in opportunities for children such as sports, music lessons, and/or field trips. Caregivers do not encourage or may even discourage children’s involvement in these activities. Conversely, caregivers push children to not only be involved but excel in activities and are demanding regarding their children’s progress.",
  },
  BPCDrugs: {
    strength:
      "Refers to caregivers’ current or past use of drugs/alcohol. Caregivers do not use drugs/ alcohol or use alcohol appropriately. Caregivers do not use illegal drugs and actively discourages children’s use of drugs/alcohol. Moderate use, if present, does not impair ability to parent.",
    baseline:
      "Refers to caregivers’ current or past use of drugs/alcohol; mostly uses alcohol appropriately. Use of drugs/alcohol does not significantly hinder the caregivers’ ability to supervise or parent children.",
    challenge:
      "Refers to caregivers’ current and/or past alcohol/substance abuse issues that negatively affect ability to parent children. Caregivers are frequently unable to care for or supervise children due to use of drugs/alcohol. Caregivers project personal issues on children or other household members; blame them for the need to drink alcohol or use other drugs.",
  },
  BPCEducation: {
    strength:
      "Refers to caregivers’ active encouragement of educational pursuits. Caregivers assure regular attendance at school and assure that children do homework and are prepared. Caregivers monitor children’s participation and progress in school; willingly attend parent/teacher conferences. Caregivers monitor children’s use of television, music, videos and the internet, screening for violent or sexually explicit materials or dangerous chat rooms and sexual predators.",
    baseline:
      "Refers to caregivers’ awareness of need for education. Caregivers will go to parent /teacher conferences but may be passive. Some attempts are made to get children to school regularly, and will admonish children to do homework, but may not be willing or able to actively support homework or school-related activities. Attempts to monitor and control media access and content may be passive or inconsistent, but parents are reasonably successful in setting limits on accessing violent or sexually explicit media content.",
    challenge:
      "Refers to caregivers’ obvious lack of support or disdain for education. May reflect caregivers’ personal negative history with school. Caregivers do not monitor school performance, do not attend conferences, blame school for academic or behavioral issues in school; may threaten teachers or school administrators. May encourage children to quit school prematurely and go to work. Caregivers are unwilling or unable to monitor media content of children, or are disinterested in doing so, allowing children to access pornography, violent movies or video games. Children are allowed to access the internet and engage strangers.",
  },
  BPCLiteracy: {
    strength:
      "Refers to caregivers’ integration of reading and writing into family’s everyday life. Reading materials evident in home. Caregivers read for pleasure and read to/with children. Caregivers and children communicate in writing when appropriate. If English is not family’s primary language, caregivers can communicate well enough to accomplish needs of everyday life, or they live in ethnic community ofsufficient size that cross-cultural communication does not adversely affect family life.",
    baseline:
      "Refers to caregivers having basic literacy skills. Can read when necessary, at a level that does not inhibit normal family life. Caregivers may not read for pleasure and may not encourage children to read regularly, but they acknowledge the need for literacy.",
    challenge:
      "Refers to caregivers’ illiteracy. Caregivers cannot read or write in English, or if English is not the primary language, caregivers basically illiterate in language of native culture. Caregivers cannot read necessary information such as medical care instructions or recipes; cannot read well enough to follow written directions, shop effectively or economically. Family may be victims of crimes that prey upon the illiterate (scams, etc.).",
  },
  BPCResilience: {
    strength:
      "Refers to caregivers’ ability to cope with stresses of everyday life; maintain positive, nurturing parenting behavior in spite of significant stressors; and to insulate children from those stressors. Caregivers exhibit problem-solving skills and are able to recover from disruption when stressors adversely affect family life. Caregivers maintain control of themselves and the family environment and are able to calmly nurture and care for children even when dealing with crises and negative life events.",
    baseline:
      "Refers to caregivers generally maintaining a positive attitude in the face of adversity, seemingly able to generally identify things that need to be fixed, but they may become frustrated or even beleaguered by significant stressors, all of which may temporarily disrupt family life. Basic problem-solving skills are evident, but caregivers may struggle to implement them.",
    challenge:
      "Refers to caregivers’ inability to deal with stress or disruptions to family life. Caregivers seem incapable of forming a problem-solving plan or exhibit problem-solving skills. Caregivers appear to be overwhelmed by stressors and become depressed or angry, discontinue nurturing behaviors, may blame the children for the onset of stressors. Caregivers lose control of their emotions, behaviors, and family life, in general.",
  },
  BPCOverall: {
    strength:
      "Refers to family receiving very high ratings in the following areas: supervision of children, disciplinary practices, provision of developmental and enrichment opportunities, use of drugs/alcohol, promotion of children’s education, controls accessto media and reading material, and literacy, displays resilience in the face of adversity.",
    baseline:
      "Refers to family experiencing some issues in the following areas: supervision of children, disciplinary practices, provisionofdevelopmentaland enrichment opportunities, use of drugs/alcohol, promotion of children’s education, controls access to media and reading material, and literacy. Caregivers exhibit adequate resiliency, but may struggle to overcome adversity; however, issues do not pose major difficulties for family members.",
    challenge:
      "Refers to family receiving very low ratings in the following areas: supervision of children, disciplinary practices, provision of developmental and enrichment opportunities, use of drugs/alcohol, promotion of children’s education, controls access to media and reading material, and literacy; adverse events are not dealt with adequately by caregivers and may throw the family into chaos or put children at risk.",
  },
  CFIBonding: {
    strength:
      "Refers to caregivers’ healthy closeness with children, and their ability to nurture children. Caregivers encourage appropriate independence for children and give love and attention freely to children. They respond to children’s needs appropriately and have a sense of attachment to children. For infants and toddlers, there is at least one primary caregiver who is consistently available, dependable, totally committed to the task of parenting, and the child responds accordingly.",
    baseline:
      "Refers to caregivers’ ability to be close to children. Caregivers may not openly encourage independence for children and may not give affection openly to children. However, children’s needs appear to be met. For infants and toddlers there is at least one primary caregiver available to provide adequate parenting and show affection for the child, and the child appears to respond accordingly.",
    challenge:
      "Refers to caregivers’ inability to form a close relationship with children and inability to nurture children. Caregivers are resentful, rejecting, or detached from children. Also refers to caregivers’ non-responsiveness, inappropriate responsiveness, or extreme enmeshment with children. For infants and toddlers, there is no primary caregiver consistently available or devoted to the child, or caregiver shows no affection for the child.",
  },
  CFICommunication: {
    strength:
      "Refers to caregivers’ use of age-appropriate and loving communication with children. word choice and voice volume are appropriate for developmental stage of children. Even when upset, caregivers maintain voice and body control and communicate effectively. Verbal communication frequently accompanied by affectionate physical contact (e.g., touching, hugging). Infants and toddlers: Non-verbal communication with infants is calm and tender; communication is accompanied by appropriate facial expressions. Verbal communications are cheerful and intended to encourage verbal skills development and language acquisition. Child responds with interest, even excitement, and child attempts to vocalize/verbalize with caregiver.",
    baseline:
      "Refers to caregivers’ basic ability to effectively communicate verbally and non-verbally with children. Caregivers may raise voice, tower over children, deliver stern lectures when upset. Caregivers may express frustration that children do not listen, when under-standing or communication style may be an issue. However, children are not at risk, and intervention is not warranted. Infants and toddlers: verbal and nonverbal efforts to communicate with the child may be less frequent than ideal, or made without accompanying facial expressions or physical contact, but communication efforts are not harmful.",
    challenge:
      "Refers to caregivers’ inability to communicate effectively with children. Communication is infrequent or absent, or constant and harping; often blaming in tone and content. Verbal and physical outbursts may occur frequently. Little or no consideration is given to children’s developmental level and ability to process and understand communication. Caregivers blame children for poor communication. Infants and toddlers: caregivers’ attempts to communicate with child are loud and/or aggressive, caregiver does not acknowledge child’s inability to comprehend due to age and stage of development, caregiver may interpret child’s inappropriate reactions or lack of reactions as willful defiance or disinterest.",
  },
  CFIExpectations: {
    strength:
      "Refers to caregivers possessing age-appropriate expectations for the children, and clear expectations of children. Above average understanding of children’s development cognitively, physically, socially and emotionally. Infants and toddlers: caregivers fully understand infants’/toddlers’ total dependence on parents to provide care and assure their well-being. Caregivers understand infants and toddlers limited abilities to communicate effectively, and interpret crying, screaming or infantile behavior as attempts to communicate or to have their needs met. Caregivers understand and react appropriately to successes as well as accidents relating to developmental processes such as toilet training and changing sleep patterns.",
    baseline:
      "Refers to caregivers’ expectations for children as mostly age-appropriate. Caregivers appear to have an average understanding of children’s developmental needs, or occasionally fail to attribute normal or age-appropriate expectations, but this behavior does not warrant intervention. Infants and toddlers: parents may not fully understand the ages and stages of child development and processes such as toilet training and changing sleep patterns. Caregivers may be frustrated by these changes, lack of progress, etc. Caregivers may not be accepting of excessive crying or infantile behavior but do not react in ways that are detrimental to the child or place the child at risk.",
    challenge:
      "Refers to caregivers having unrealistic and unclear expectations for the children. Do not tolerate mistakes in children. Children are expected to take on adult responsibilities. Or, children are not allowed to engage in age-appropriate behaviors (e.g. sports, dating). Little or inappropriate understanding of normal child development. Infants and toddlers: caregivers are intolerant of normal processes associated with age and stage of development, especially with regard to toilet training, excessive crying, infantile behavior and changing sleep patterns, etc. Caregiver interprets normal child development and skill acquisition as abnormal or willfully disobedient and expresses anger or inflicts discipline on child inappropriately.",
  },
  CFISupport: {
    strength:
      "Refers to excellent emotional and/or physical support within the family. Physical support is given when needed, such as providing daycare, transportation, or financial help. Family members appear to help each other willingly and know they can rely on one another in emergencies.",
    baseline:
      "Refers to good support within the family. Some physical support is provided when re-quested by a family member. Most requests for help from family members are met by other family members.",
    challenge:
      "Refers to poor or lack of emotional support or physical support among family members. Family does not provide transportation, day care, or financial assistance when needed. Undermining of each other in the family. Family members do not tolerate success by other family members.",
  },
  CFIParents: {
    strength:
      "Refers to relationship between caregivers as stable, consistent, affectionate, and loving. Caregivers are able to communicate clearly and encourage each other. Caregivers maintain a healthy separateness from children with clear parent/child boundaries.",
    baseline:
      "Refers to relationship between caregivers. Some conflicts may be evident, but do not appear to be leading to divorce, separation, or abandonment. Some minor difficulties with communication but do not significantly impair the relationship or care and raising of children.",
    challenge:
      "Refers to relationship between caregivers as unsupportive and unstable, or hostile. Major communication difficulties with evidence of discord, violence, or indifference. Divorce, separation, or abandonment are prominent issues. Boundaries are not clearly maintained between partners, or between the couple and children.",
  },
  CFIRoutines: {
    strength:
      "Refers to caregivers’ use of daily routines to facilitate family activities at the beginningand end of the day. May involve hygiene, meals, chores, work/childcare preparation.Provides structure and expectations for both caregivers and child. Flexibility for specialoccasions. Children participate in age-appropriate ways. There is regular observanceby family of rituals that may include birthdays, holidays and expressions of faith. Ritualsfoster sense of security and belonging for children.",
    baseline:
      "Regular family activities and routines are present that generally serve to meet family’s daily needs. Routines may break down or become too rigid, becoming obstacles rather than aids to smooth family life. Family celebrates some rituals but may not integrate observances into everyday life. Children are usually included in rituals but may not have active role in observance. Basic sense of security and belonging is present.",
    challenge:
      "Refers either to an absence of routines leading to chaos in family and daily struggle to begin/end day’s activities, or to routines so rigid and inflexible that family members become resentful or rebellious. Observance of birthdays or other important days or events is sporadic or inconsistent. Lack of rituals leads to feelings of exclusion by children. Participation in rituals that are not age appropriate, or are violent, leading to fearful or violent behavior in children.",
  },
  CFIRecreation: {
    strength:
      "Refers to family having regular time for recreation or play together. May involve sports, picnics, outings to park/zoo, camping, or board games, cards, puzzles, etc. Recreation planning takes all family members’ needs and desires into account and activities are age appropriate. Recreation is balanced with work and household activities; fosters healthy family relations.",
    baseline:
      "Refers to family having some recreation time together. Recreation or play may be slightly out of balance with work or household chores, but imbalance does not adversely affect meeting family’s basic needs, and relationships are basically healthy.",
    challenge:
      "Refers to family either having no recreation or play time together, or recreation time that is geared totally to adult needs or is clearly inappropriate for children. Recreation or play is way out of balance with work or chores: either no recreational outlets or recreation at the expense of meeting family’s basic needs.",
  },
  CFIOverall: {
    strength:
      "Refers to family receiving very high ratings in the following areas: bonding with children, communication with children, expectations of children, mutual support, relationship of parents/caregivers, routines and rituals, recreation and play activities.",
    baseline:
      "Refers to family receiving ratings of adequate in the following areas: bonding with children, communication with children, expectations of children, mutual support, relationship of parents/caregivers, routines and rituals, recreation and play activities.",
    challenge:
      "Refers to family receiving very low ratings in the following areas: bonding with children, communication with children, expectations of children, mutual support, relationship of parents/caregivers, routines and rituals, recreation and play activities.",
  },
  DFSViolence: {
    strength:
      "Refers to families in which violence has never occurred between caregivers, and all family members are encouraged to solve issues nonviolently. Also refers to families in which domestic violence has occurred, but no longer occurs due to family’s success in counseling, and family actively discourages violence.",
    baseline:
      "Refers to families in which domestic violence has occurred, but no longer occurs. Family is involved in counseling and making some progress. Also, families in which violence has never occurred. Disputes occur, and family members solve issues without violence.",
    challenge:
      "Refers to incidents, complaints, or arrests for domestic violence. Violence between care-givers negatively affects ability to parent and/or has resulted in physical or emotional harm to children. One caregiver lives in fear of the other, and/or children fear for safety of one caregiver or themselves.",
  },
  DFSConflict: {
    strength:
      "Refers to families having strong bonds among themselves and with extended family members. Family members and relatives are likely to serve as mediating resources or safe havens if family violence were to occur.",
    baseline:
      "Family members and relatives generally get along but may become belligerent or combative when consuming alcohol or discussing sensitive or values-laden topics. Some family members are appropriately avoided, based on known histories, so that they do not pose a threat to children or children’s immediate family.",
    challenge:
      "Intra-familial fights may occur, or family factions may feud, sometimes with physical violence among family members occurring. Relatives may show up uninvited and unwanted when intoxicated or when in trouble, posing a threat to family and children’s safety. Nefarious relatives may expose caregivers’ children to danger, drugs, illegal activities.",
  },
  DFSPhysical: {
    strength:
      "Refers to families in which incidents or substantiated complaints of physical abuse have never occurred, or they have occurred and family has successfully been involved in counseling. Caregivers do not condone violence. Caregivers successfully manage anger and discipline responsibly and without physical punishment. Caregivers actively protect children from physical abuse by people outside the family.",
    baseline:
      "Refers to familiesin which physical abuse has not occurred, or in which complaints, incidents, orsubstantiations of abuse by caregivers have occurred, butsatisfactory progress is being made through counseling or the provision of otherservices. Caregiverssuccessfully manage anger most of the time, and discipline normally is not physical and is never excessive. Caregivers passively protect children from physical abuse by people outside the family.",
    challenge:
      "Refers to incidents, complaints, or substantiations of physical abuse by caregivers which have not been acknowledged or addressed, or they have been resolved unsatisfactorily. Caregivers may be actively denying substantiated abuse and/or neglect, or actively resisting intervention. Caregivers cannot manage anger and react violently or irrationally toward children. Physical punishment is excessive or bizarre and meets definition of abuse. Caregivers flagrantly fail to protect children from abuse by people outside the family.",
  },
  DFSEmotional: {
    strength:
      "Refers to families in which incidents or substantiated complaints of emotional abuse have never occurred, or they have occurred and the family has been successfully involved in counseling. Caregivers do not demean or denigrate children. Children exhibit secure feelings and sense of self-worth. Caregivers actively protect children from inappropriate criticism by people outside the family.",
    baseline:
      "Refers to families in which emotional abuse has not occurred, or in which complaints, incidents, or substantiations of emotional abuse by caregivers have occurred, but satisfactory progress is being made through counseling or the provision of other services. Caregivers successfully manage anger most of the time, and rarely or never denigrate or belittle children. Caregivers passively protect children from emotional abuse by people outside the family.",
    challenge:
      "Refers to incidents, complaints, substantiations of emotional abuse by caregivers which have not been acknowledged or addressed, or they have been resolved unsatisfactorily. Caregivers may be actively denying substantiated emotional abuse, or actively resisting intervention. Caregivers cannot manage anger and react with demeaning tirades towards children. Caregivers berate or denigrate children with apparent emotional injury to children. Caregivers flagrantly fail to protect children from similar abuse by people outside the family. Children exhibit emotional disturbance as a result of maltreatment.",
  },
  DFSSexual: {
    strength:
      "Refers to children who have never experienced sexual abuse, and who have learned about such concepts as good and bad touch. Or, children who have experienced sexual abuse, and are now being protected. Children are in treatment and have been making excellent progress.",
    baseline:
      "Refers to children who have never experienced sexual abuse but have not been actively taught concepts such as good or bad touch, leaving children potentially vulnerable to future abuse. Or, children who have been sexually abused, but are making satisfactory progress in treatment.",
    challenge:
      "Refers to children having experienced sexual abuse by others, or children who have sexually abused others. May be inferred or substantiated. Children have been referred for treatment or are in treatment. A judgement is made regarding unsatisfactory progress in treatment. Sexual abuse is ongoing, or risk of sexual abuse is high. Caregivers do not protect children from situations where there is an elevated risk of sexual abuse or exploitation from within or outside the family.",
  },
  DFSNeglect: {
    strength:
      "Refers to families in which incidents or substantiated complaints of neglect have neveroccurred or have occurred but outstanding progress in counseling is observed. Care-givers recognize and are successful in meeting children’s physical, social, medical, andemotional needs. Basic needs for shelter, food, health care, supervision, and educationare always met. Adults and children are clean, well-groomed and take pride inthemselves and their appearance.",
    baseline:
      "Refers to families in which incidents or substantiated complaints of neglect have never occurred or have occurred but some progress in counseling is made for the family, and children are safe. Caregivers usually recognize physical, social, medical, and emotional needs of children and meet most of these needs. Oversights by caregivers do not pose undue risk to children. Basic needs for shelter, food, health care, supervision, and education are regularly met. Children may occasionally appear unkempt, but not to the degree that it causes issues for the family or children.",
    challenge:
      "Refers to incidents or substantiated complaints of child neglect by caregivers which have not been acknowledged or addressed, or they have been resolved unsatisfactorily. Caregivers do not recognize or deny children’s physical, social, medical, or emotional needs. Basic needs forshelter, food, health care, supervision, or education are frequently not met. Caregivers may be actively denying substantiated neglect, or actively resisting intervention. Children are frequently or constantly unkempt, dirty, and/or have body odor. Clothing is dirty, inappropriate for season, and there is an absence of awareness of personal hygiene.",
  },
  DFSWeapons: {
    strength:
      "Refers to the caregivers’ proactive protection of children from access to weapons that could harm the children or that children could use to harm others. Guns, knives, etc. are locked in safes, cabinets, or drawers, as appropriate. Ammunition is kept separate from guns. Children are taught about the dangers of weapons and how to avoid contact with weapons when away from the home.",
    baseline:
      "Refers to caregivers’ awareness of need to protect children from access to weapons, particularly firearms. Caregivers have taken some precautions to protect children from access; caregivers may not be fully knowledgeable about how to protect children. Children are taught about dangers of weapons.",
    challenge:
      "Refers to caregivers’ flagrant lack of concern about protecting children from weapons. Weapons are not secure and are in areas where children might gain access. Children have potential to discover weapons or to use them during play. Children are not taught about dangers of weapons and may be encouraged to handle weapons for caregivers’ amusement.",
  },
  DFSOverall: {
    strength:
      "Refers to families receiving very high marks in the following areas: absence/presence of domestic violence between caregivers, family conflict; physical or emotional abuse of children, sexual abuse of children, neglect of children, and access to weapons.",
    baseline:
      "Refers to families receiving baseline ratings in the following areas: absence/presence of domestic violence between caregivers, family conflict; physical or emotional abuse of children, sexual abuse of children, neglect of children, and access to weapons.",
    challenge:
      "Refers to families receiving very negative marks in the following areas: absence/ presence of domestic violence between caregivers, family conflict; physical or emotional abuse of children, sexual abuse of children, neglect of children, and access to weapons.",
  },
  ECWBehavior: {
    strength:
      "Refers to children being well-behaved as appropriate to age, and there are no discipline issues. Children viewed as cooperative, following rules and doing chores. Infants and toddlers are on track behaviorally with respect to age and stage of development; exhibit behaviors indicating wanting to be with caregiver and responding appropriately to caregivers, appropriately shy of strangers or dangerous or surprising events or circumstances, but they accept care and comfort from known substitute caregivers.",
    baseline:
      "Refers to some issues in managing children’s behavior, and some discipline issues. Children are usually cooperative, have some difficulties in following rules or completing chores, but difficulties do not merit intervention. In the normal course of development, children may be rebellious or question family leadership, but issues are resolved and children avoid confrontation with the law. Infants and toddlers are generally on track behaviorally with respect to age and stage of development; they are accepting of caregiver and generally react appropriately to others.",
    challenge:
      "Refers to challenges managing children’s behavior at home and/or in school. Totally un-cooperative. Refuse to follow rules or do chores. Highly oppositional behaviors. Exhibit antisocial behavior; possible emotional disturbance, child seems beyond caregiver control. Infants and toddlers lack appropriate affective expression towards caregiver, rejects caregiver efforts to provide care/comfort; appears listless or withdrawn; are extremely delayed with normal development such as toilet training; exhibit volatile outbursts of crying or anger and appear to be inconsolable for extended periods of time.",
  },
  ECWSchoolPerformance: {
    strength:
      "Refers to children having excellent attendance at school and excellent academic records. Children like school and/or behave appropriately in school.",
    baseline:
      "Refers to children having good attendance and an academic record that is average or appropriate to children’s capabilities. Some behavior issues may be evident in school, but they do not result in suspension or expulsion.",
    challenge:
      "Refers to children having poor attendance at school, a poor academic record and/or many behavior issues in school. Children profess to hate school and/or avoid school with illnesses or truancy. Children serve frequent detention and may be suspended or even expelled from school.",
  },
  ECWChildParent: {
    strength:
      "Refers to children accepting discipline and supervision. Having open and clear communication with caregivers. Express or exhibit strong affiliation with caregivers.",
    baseline:
      "Refers to children having some issues in accepting discipline and supervision. Also, some issues in communication with caregivers, but this doesn’t warrant intervention.",
    challenge:
      "Refers to discipline and supervision challenges with children. Lack of open and clear communication, or no communication with caregivers. Do not respect boundaries and have an abusive or hostile relationship with caregivers. Express desire to leave family as soon as possible.",
  },
  ECWChildPeers: {
    strength:
      "Refers to getting along well with siblings and peers. Siblings help one another when in need. Infrequent fights or issues with siblings or peers. Siblings can play together. Play with peers is constructive and friendly. Children are cared for and protected by older siblings and treat younger siblings accordingly. Peers support one another socially and emotionally. Peers generally selected from prosocial sources, known community or neighborhood sources. Children exhibit good judgment when choosing peers to befriend.",
    baseline:
      "Refers to getting along for the most part with siblings and peers. Some fights occur among siblings, and siblings do not play together frequently. Children may not always exhibit the best judgment when choosing peers to befriend, resulting in friendship tensions or fights. Child may have few peers as friends, but they are prosocial. On the whole, issues among siblings and peers do not merit special attention.",
    challenge:
      "Refers to frequent fights and inability to get along with siblings or peers. No support to or from siblings. Intense rivalry, conflict, and/or scapegoat of siblings and/or peers. Fights may result in injury, or other behavior may result in emotional damage to siblings or peers. Older siblings may have physically or sexually abused younger siblings, or neglected younger sibling left in their care. Children exhibit poor judgment when selecting peers to befriend, resulting in negative or destructive behaviors and peer relationships. Children may seek friendships with persons much older or younger than themselves, and they may behave inappropriately to attain those friendships.",
  },
  ECWMaintain: {
    strength:
      "Refers to children wanting to stay with family and caregivers and be part of the family. Children are motivated to change behaviors and cooperate in order to maintain a positive and cohesive family environment.",
    baseline:
      "Refers to children’s general interest in staying with family and caregivers. Children may need extra encouragement to change behaviors and cooperate, being willing to tolerate some level of family discord. Stages of child development are accompanied by opposition to family rules and caregivers’ expectations, but when pressed, children will accept interventions or services.",
    challenge:
      "Refers to children’s lack of interest in staying with family/caregivers. Children are not motivated to change behaviors and do not want to cooperate. Children are against any intervention or services, or children have strong desire to leave family for self-serving reasons. Children may run away from home for days or weeks at a time. Children rebel against any effort by caregivers to supervise children and are secretive about peers, activities, or whereabouts.",
  },
  ECWSubstances: {
    strength:
      "Refers to children’s avoidance of the use of alcohol, cannabis, or other medical/recreational drugs. As appropriate to age, children understand the risks of drug use at an early age. As appropriate to age, children know how to avoid persons who use drugs and how to resist peer/social pressure to use drugs.",
    baseline:
      "Children have minimally adequate knowledge of the risks associated with drug use, but they may not be wholly committed to avoiding drugs now or in the future. Children may have been exposed to alcohol and other drugs, but they do not use them now and know not to use them. Children may be concerned about the allure of drugs and be willing to accept counseling or treatment.",
    challenge:
      "Refers to children actively engaged in the use of alcohol and other drugs, or actively seeking that engagement. Children do not believe evidence of serious risks and consequences of drug use, and/or they may hold serious misconceptions about dangers of drug use. Children fantasize about future acquisition and use of alcohol/drugs. Children resist any efforts to educate or efforts of pre-emptive counseling. Contributing factor may be that parent/caregiver regularly misuses alcohol or drugs.",
  },
  ECWCompetence: {
    strength:
      "Refers to children exhibiting age-appropriate self-regulation of emotions and behavior. Children exhibit age-appropriate ability to solve problems, communicate their feelings and needs respectfully and nonviolently, and de-escalate confrontations with caregivers, peers and others. Children exhibit prosocial behaviors such as adhering to rules when playing and exhibiting a friendly demeanor. Infants and toddlers: show signs of developing appropriate social/emotional awareness, eager for caregiver contact, appropriately shy of non-caregiver contact as infants, less so for toddlers as comfort levels are established and reinforced by caregivers. Toddler can interact with other toddlers socially, such as sharing toys during play.",
    baseline:
      "Refers to children exhibiting adequate self-regulation of emotions and behavior, though occasionally children may be confrontational but unable to communicate effectively. Children may become argumentative but not aggressive with caregivers, peers and others. Children may sometimes struggle emotionally or behaviorally, but generally are on track with respect to their age and stage of development. Infants and toddlers may show fear of strangers, but they will calm if caregiver displays comfort/confidence in stranger contact with child; may display some difficulty engaging with strangers and/or other children, but they appear to be acquiring adequate skills for social interactions.",
    challenge:
      "Refers to children exhibiting an inability to control emotions or behavior. Children display age-inappropriate reactions to stress or social issues, may throw temper tantrums or become combative when parents attempt to regulate child’s emotions/behaviors, and/or become aggressive when disagreeing with peers or facing other social stressors. Anger and aggression are used instead of problem-solving and social/emotional engagement. Children may appear consistently or chronically sad, frustrated or angry. Infants and toddlers: show fear/avoidance of caregivers and other adults, show extreme resistance to being held, soothed, comforted by substitute care providers or strangers; toddlers unable to engage socially with other toddlers, show fear, react aggressively, refuse to share, may bite, kick or hit other toddlers in social settings accompanied by emotional outbursts.",
  },
  ECWOverall: {
    strength:
      "Refers to family receiving very high ratings in the following areas: children’s behavior; school performance; relationship with caregivers; relationship with siblings; relationship with peers; and cooperation and motivation to maintain the family. Children exhibit age-appropriate social and emotional competence. No challenge ratings in these areas.",
    baseline:
      "Refers to family receiving adequate ratings in all of the areas: children’s behavior; school performance; relationship with caregivers; relationship with siblings; relationship with peers; and cooperation and motivation to maintain the family. Social/emotional competence is adequate but there may be some inconsistent/unpredictable behaviors as these competencies are acquired.",
    challenge:
      "Refers to family receiving very low ratings in the following areas: children’s behavior; school performance; relationship with caregivers; relationship with siblings; relationship with peers; and cooperation and motivation to maintain the family. Social/emotional competence is lacking, evidenced by extreme emotional reactions to even common stressors, and antisocial or asocial reactions when provoked.",
  },
  FYWBehavior: {
    strength:
      "Refers to youths being well-behaved as appropriate to age, no discipline issues. Youths are cooperative, following rules and doing chores. Youths exhibit minimum externalizing behaviors (aggression, disruptive behaviors, impulsivity) and internalizing behaviors (depression, anxiety, mood/thought issues)",
    baseline:
      "Refers to some issues in managing youth’s behavior, and some discipline issues, but not out of line with normal development. Youths are usually cooperative, may exhibit some difficulties following rules or completing chores, but difficulties do not merit intervention. In the normal course of development, youths may occasionally question caregiver leadership, but issues are satisfactorily resolved.",
    challenge:
      "Refers to challenges managing youth’s behavior at home and/or in school. Totally un-cooperative. Refuse to follow rules or do chores. Highly oppositional or antisocial behavior; possible emotional disturbance, youths seem beyond caregivers’ control. Youths may engage in delinquency, gang-related activities, or provocative appearance (e.g. tattoos, outlandish dress, excessive makeup, body piercings). Youths have a history of, and inclination to commit status offenses (e.g., runaway, truancy) or more serious, chargeable offenses (e.g., burglary, larceny, destruction of property). Youths are engaged in dangerous or self-injurious behavior (e.g., extreme risk-taking, early sexual activity).",
  },
  FYWSchoolPerformance: {
    strength:
      "Refers to youths having excellent attendance at school, and excellent academic records in line with youths’ general capabilities. Youths like school and/or behave appropriately in school. Appropriate to age, youths recognize the need for employment skills and engage in education or vocational training towards that end. Youths have achieved competence in literacy and numeracy.",
    baseline:
      "Refers to youths having good attendance and average academic records appropriate to youths’ capabilities. Some behavior issues may be evident in school, but they do not result in suspension or expulsion. Youths are making adequate progress towards skills/employment and labor force attachment.",
    challenge:
      "Refers to youths having poor attendance at school, a poor academic record and/or many behavior issues in school. Youths profess to hate school and/or avoid school with illnesses or truancy, may have quit school or threaten to quit. Youths serve frequent detention and may be suspended or even expelled from school. Youths express no interest in acquiring employment skills, eschew vocational training, may brag about expecting to pursue a criminal lifestyle as adults.",
  },
  FYWChildParent: {
    strength:
      "Refers to youths accepting discipline, guidance and supervision. Youths have open and clear communication with caregivers. Youths express or exhibit strong affiliation with caregivers.",
    baseline:
      "Refers to youths having some issues in accepting discipline and supervision. Also, some issues in communication with caregivers. Confrontations are not hostile or violent and are normally resolved without incident. Issues do not warrant intervention.",
    challenge:
      "Refers to serious disciplinary and supervision challenges with youths. Lack of open and clear communication, or no communication, or deceitful with caregivers. Youths do not respect boundaries and have an abusive or hostile relationship with caregivers. Youths express disdain or hatred towards caregivers.",
  },
  FYWChildPeers: {
    strength:
      "Refers to youths getting along well with siblings and peers. Siblings/peers help one another when in need. Sibling/peer relationships are constructive and friendly. Youths are protective of younger siblings. Youths demonstrate good judgment when selecting peers to befriend, generally selecting them from known, prosocial sources in the community or neighborhood. Youths are a positive influence on one another. Peer groups engage in prosocial activities such as sports, civic activities, clubs or other organized activities.",
    baseline:
      "Refers to youths getting along for the most part with siblings and peers. Some fights occur among siblings, and siblings do not interact frequently. Youths do not always exhibit good judgment when selecting peers to befriend, resulting in strained friendship/negative interactions, but youths are learning from their mistakes. Youths may have few peers as friends, but those few are generally prosocial. On the whole, issues among siblings and peers do not merit special attention.",
    challenge:
      "Refers to youths engaging in frequent fights and displaying an inability to get along with siblings or peers. Intense rivalry, conflict, and/or scapegoat of siblings and/or peers. Fights may result in injury, or other behavior may result in emotional damage to siblings or peers. Older siblings may have physically or sexually abused younger siblings, or neglected younger sibling left in their care. Peers may exhibit negative or destructive behaviors and peer relationships are not positive or supportive. Youths may seek friendships with persons much older or younger than themselves, and they behave inappropriately to attain those friendships. Youths may seek peers engaged in anti-social or criminal lifestyles; or peers/peer groups that idolize criminals (e.g., murderers, mass shooters), or extreme political/religious/cultural ideologies (e.g., Nazism, white supremacy). Youths may seek or have criminal gang affiliations.",
  },
  FYWMaintain: {
    strength:
      "Refers to youths wanting to stay with family/caregivers and be part of the family; and/or create and maintain positive relationships with adult mentors that will carry on into adulthood. Youths are motivated to change behaviors and cooperate in order to maintain a positive and cohesive family environment. Older youths seek to acquire skills/ experience needed to transition to independence by becoming self-supporting and avoiding confrontation with the law.",
    baseline:
      "Refers to youths’ interest in staying with family/caregivers. Youths are passive about changing behaviors in order to minimize family discord, but they can be motivated to do so. Stages of youth development accompanied by opposition to family rules and caregivers’ expectations, but when pressed, youths will accept interventions/ services/skills training. Youths may be passive about developing relationships with trusted, mentoring adults, but can be motivated to do so.",
    challenge:
      "Refers to youths’ firm lack of interest in staying with family/caregivers. Youths are not motivated to change behaviors and do not want to cooperate. Youths eschew any efforts to develop relationships with trusted adult mentors. Youths express strong desire to be independent without developing a plan for acquiring skills for employment necessary to live independently. Youths are against any intervention or services, and they profess a strong desire to leave family for self-serving reasons. Youths may run away from home for days or weeks at a time and rebel against any effort by caregivers to supervise youths. Youths are secretive about peers, activities and whereabouts. Youths may run away or be homeless due to parent/caregiver cutting ties.",
  },
  FYWSubstances: {
    strength:
      "Refers to youths’ avoidance of the use of alcohol, cannabis, or other medical/recreational drugs. Youths understand the risks of addiction, including lethal overdose. Youths exhibit internalized belief in the avoidance of substances and understand their detrimental effects. Youths know how to avoid persons who use drugs and how to resist peer/social pressure to use drugs.",
    baseline:
      "Youths have minimally adequate knowledge of the risks associated with drug use, but they may not be wholly committed to avoiding drugs now or in the future. Youths may have experimented with alcohol and other drugs, does not use them now, and knows not to use them, particularly addicting and dangerous drugs. Youths may be concerned about the allure of drugs and be willing to accept counseling or treatment.",
    challenge:
      "Refers to youths engaging in the reckless abuse, chronic use, or excessive use of alcohol and other drugs. Youths eschew evidence of serious risks and consequences of drug use. Youths select peers based, in part, on shared interests in obtaining and using alcohol and other drugs. Youths may commit crimes to obtain drugs. Youths may fantasize about the criminal lifestyle associated with selling drugs. Youths may be active users of drugs, even be addicted, and refuse any effort to provide counseling or drug treatment.",
  },
  FYWCompetence: {
    strength:
      "Refers to youths exhibiting age-appropriate resilience, including self-regulation of emotions and behavior, self-control and emotional stability. Youths exhibit age-appropriate reasoning and problem-solving. Youths communicate their feelings and needs respectfully and nonviolently, are appropriately assertive, cooperative and empathetic. They can de-escalate confrontations with caregivers, peers and others. Youths seek in-person social interactions and recreation/play and maintain a healthy balance between in-person and virtual interactions (texting, gaming, social media, etc).",
    baseline:
      "Refers to youths exhibiting adequate self-regulation of emotions and behavior, though occasionally they may be confrontational and/or unable to communicate effectively. Youths may become argumentative but not aggressive with caregivers, peers and others. Youths may sometimes struggle emotionally or behaviorally, but generally are socially/emotionally on track with respect to their age and stage of development. Youths may prefer virtual interactions over in-person interactions, but they can be motivated to increase in-person interactions.",
    challenge:
      "Refers to youths being out of control emotionally/behaviorally. Youths display seriously age-inappropriate reactions to stress or social issues, may throw temper tantrums or become combative when caregivers’ attempts to regulate youths’ emotions/behaviors. Youths may exhibit burgeoning anti-social personality traits such as consistently blaming others for negative events or consequences, failing to accept responsibility for their own decisions/actions. Youths are reclusive, eschewing in-person interactions, relying instead on virtual/electronic social interactions, especially frequent gaming (esp. violent gaming) and frequent visits to anti-social internet sites. Anger and aggression are used instead of problem-solving and social/emotional engagement. Youths may appear to be either consistently or chronically sad/frustrated/angry, or agitated and hyper-vigilant about wrongs being done to them by others.",
  },
  FYWOverall: {
    strength:
      "Refers to family/youth receiving very high ratings in the following areas: youths’ behavior; school performance; vocational/transitional preparation; relationships with caregivers, siblings; peers. Youths are also cooperating and motivated to maintain the family and affecting an orderly transition to independent adulthood. Youths are avoiding drugs and exhibiting normal social/emotional development and resilience. Youths are developing good in-person social skills. No challenge ratings in these areas.",
    baseline:
      "Refers to family/youths receiving adequate ratings in all of the areas: behavior; school performance; work/independence preparation; relationship with caregivers, siblings, peers; and cooperation and motivation to maintain the family while preparing to transition to independent adulthood. For the most part, youths are avoiding alcohol and other drugs and are exhibiting acceptable social/emotional development and resilience, including adequate in-person social skills.",
    challenge:
      "Refers to family/youth receiving very low ratings in the following areas: children’s behavior; school performance; preparation for independent adulthood; relationship with caregivers, siblings; peers; and cooperation and motivation to maintain the family. Youths have destructive peer relationships, are involved alcohol and other drugs as users, sellers or both. Youths are leaning behaviorally towards criminal or anti-social lifestyles; and do not exhibit adequate social/emotional development. Youths rely mostly or solely on virtual social interactions such as gaming and social media.",
  },
  GSCLSocial: {
    strength:
      "Refers to family that has frequent interactions with relatives, neighbors and friends. These people are accessible and are a positive influence on family life. Few negative social relationships and if these occur, they are handled in a timely manner and are not allowed to continue. Family can always rely on friends, relatives or neighbors for help in emergency.",
    baseline:
      "Refers to family having some interactions with relatives, neighbors, and friends. Individuals may provide positive and/or negative influences on the family. When negative, these influences are dealt with appropriately and do not pose a serious threat to family safety. Family can sometimes rely on these relationships for help in an emergency.",
    challenge:
      "Refers to family being socially isolated and having little or no contact with neighbors, friends or relatives. Or, these persons are a strong negative influence on caregivers or children. Social contacts may revolve around illegal activities such as drug use, prostitution or gambling. Family makes little or no effort to participate in activities that provide opportunities for positive social relationships. Family cannot rely on relatives, neighbors or friends for help during an emergency.",
  },
  GSCLSchool: {
    strength:
      "Caregivers’ relationship with schools, childcare providers, and other child serving organizations (e.g., sports, youth groups) is open, respectful, frequent and honest. Caregivers, teachers or service providers communicate clearly and encourage each other’s success. Interactions focus on and advocate for the best interest of the children.",
    baseline:
      "Relationship between caregivers and school, childcare, or other youth service provider is adequate to ensure children’s safety and is respectful. Minor difficulties in communications or advocacy may occur but do not significantly impair relationship.",
    challenge:
      "Relationship between caregivers and schools, childcare or youth service providers is un-supportive, critical, disrespectful, hostile, dishonest, or nonexistent. Communication does not focus on best interest of children but may focus on caregivers’ convenience or caregivers’ interest at expense of children’s participation and success.",
  },
  GSCLCommunity: {
    strength:
      "Refers to family’s connection to civic and cultural affairs in the neighborhood and community. Adult family members vote and are knowledgeable about issues affecting community. Family identifies with cultural or ethnic segments in the community and promotes welfare of all children and families. Civic involvement is a positive influence on family life and does not detract from family needs or relationships.",
    baseline:
      "Family is involved in community to a limited degree. Caregivers may attend some community meetings, are likely to vote, but family does not invest time in larger social issues. Family identifies with some ethnic or cultural groups but not others, and caregivers may express suspicion or mistrust of other ethnicities or cultures. Family may be selective about community involvement based on culture or ethnicity.",
    challenge:
      "There is little or no sense of neighborhood, or family feels alienated in neighborhood and community. Family members afraid to interact with neighbors. Caregivers do not participate in civic meetings or exercise voting rights, are not knowledgeable about social or political issues affecting them. Or, civic involvement is confrontational with respect to politics or other ethnicities or cultures, is damaging to family relationships or safety, and interferes with family’s ability to function in the neighborhood or community.",
  },
  GSCLSpiritual: {
    strength:
      "Family participates in family-centered spiritual celebrations. Family receives strength and encouragement from the spiritual community. Family connects with others sharing similar beliefs and appears to integrate beliefs into daily living. Children appear to be connected to and supported by spiritual community.",
    baseline:
      "Caregivers identify a connection with a particular spiritual community, but they may be sporadic in investment of time or energy into spiritual community. Or, caregivers may not choose spiritual involvement, but they have other community supports for their family.",
    challenge:
      "Family has had support from a spiritual community in the past but may be alienated or disengaged at this time. Family recognizes they need spiritual support but do not know how to re-engage.",
  },
  GSCLHelp: {
    strength:
      "Refers to family accepting help orsupport from extended family, neighbors or community when offered to the family. Caregivers will take initiative to provide basic needs for family. Caregivers will seek help when needed and are able to accept help without shame or guilt. Caregivers anticipate being able to help others when in a position to do so.",
    baseline:
      "Refers to family being willing to accept some help from agencies, relatives or friends, but may not actively seek assistance. Caregivers may be willing to do without some basic needs or services to avoid asking for help. Caregivers may feel guilt or shame for accept-ing help, but these feelings do not prevent accepting help if it is offered from outside, or the need is sufficiently great.",
    challenge:
      "Family is not motivated to, or actively avoids seeking help from agencies, relatives or friends when in need. Even if family life is deteriorating markedly, caregivers have refused help when offered in recent past, to the continued detriment of family life and children well-being. Family is suspicious of help, and may fear future consequences of accepting help, such as obligation to reciprocate. Caregivers may suffer overwhelming feelings of guilt or shame preventing acceptance of help.",
  },
  GSCLOverall: {
    strength:
      "Refers to family receiving very high ratings in the following areas: social relationships, relationships with childcare, schools and extracurricular services, connection to neighborhood and to cultural/ethnic community, connection to spiritual and religious community, caregivers’ initiative and acceptance of available help and support.",
    baseline:
      "Refers to family receiving adequate ratings in all of the areas: social relationships, relationships with childcare, schools and extracurricular services, connection to neighborhood and to cultural/ethnic community, connection to spiritual and religious community, caregivers’ initiative and acceptance of available help and support.",
    challenge:
      "Refers to family receiving very low ratings in the following areas: social relationships, relationships with childcare, schools and extracurricular services, connection to neighborhood and to cultural/ethnic community, connection to spiritual and religious community, caregivers’ initiative and acceptance of available help and support.",
  },
  HSSEmployment: {
    strength:
      "Refers to family having stable, legal employment over the past 12–24 months. Employer provides benefits, such as health insurance, and employer respects caregivers’ need to attend to and spend time with family. Caregiver takes advantage of opportunities for training and advancement.",
    baseline:
      "Refers to family having relatively stable, legal employment in the past 12 months. Employment experience may vary between periods of steady employment, layoffs or compulsory overtime that create occasional disruption to family routines or caregiver’s availability to family. Benefits are not available or are available at very high cost.",
    challenge:
      "Refers to caregiver losing employment for negative reasons (such as being fired, laid off for substance use or poor attendance) two or more times in the past 12 months. Caregivers work only sporadically by choice, placing extreme stress on family finances. Family is without benefits of any kind. Caregivers’ employment may be illegal (unreported earnings, drug trade, prostitution). Caregivers not interested or unable (perhaps due to illiteracy) to participate in advancing employment options.",
  },
  HSSIncome: {
    strength:
      "Family has had stable and sufficient income over the past 12–24 months. More than enough income to pay for food, housing and clothing. Money is not an issue. Family has money to meet responsibilities and spend on leisure activities and recreation.",
    baseline:
      "Income is sufficient in meeting basic needs, such as food, housing and clothing. There are some money pressures, such as credit card debt, but they do not significantly inhibit family activities or prevent purchase of necessities. Caregivers may worry about having sufficient income and may skimp on some necessities.",
    challenge:
      "Family has seriously insufficient income and exhibits inability to pay for food, housing and/or clothing. Family receives public assistance as primary source of income, and/or primary caregivers are unemployed. Money is a major issue. Child support is not paid. Public assistance time limits have expired. Family does not have money to meet basic needs.",
  },
  HSSBudgeting: {
    strength:
      "Refers to family using money in a way that provides benefits financially, and family has clear spending plans or priorities. Debts are small and manageable. There is a planned use of money, and no back bills. Family is good at bargain hunting and sets aside money for recreation, leisure, gifts (e.g., birthdays).",
    baseline:
      "Refers to family having debts, but debts are not sufficiently large or in default such that creditors are taking action against family. Some issues with budgeting, but these do not prevent family from meeting basic needs for food, housing, etc. Leisure or recreation is sporadic and occurs when extra money appears to be available, rather than as part of a spending plan.",
    challenge:
      "Refers to family being severely in debt. Family may have a history within the past year of being evicted from their home due to nonpayment. Great difficulty paying bills, and/or bills are paid late. May have large credit card or loan shark debt. Chaotic budgeting, and family is constantly in crisis over money. Caregivers frequently broke, maybe due to gambling or substance use. There is no budget plan. Caregivers’ leisure or luxuries are bought before necessities; children often without basic needs being met.",
  },
  HSSNutrition: {
    strength:
      "Refers to caregivers’ awareness of nutritional needs of children, including any special needs (e.g. food allergies). Meets those needs. Prepares balanced, nutritious meals. Care-givers may also provide vitamins or supplements. Ample food available. Children eat on a regular schedule. Food/nutrition actively monitored by caregivers.",
    baseline:
      "Family meets basic nutritional needs. Children have access to sufficient and varied food, though individual meals may not always be balanced. Family may rely on fast foods a little too frequently as main source of nutrition. Children may consume excess junk foods, particularly when away from home.",
    challenge:
      "Refers to caregivers’ lack of awareness of nutritional needs of children, including any special needs. Does not attempt to meet nutritional needs. Does not consider food preparation important. Inadequate supply of food, and/or inappropriate food. Lots of junk food consumed. Children often go hungry. Obesity, diabetes, or other health issues related to poor nutrition are apparent in caregivers and/or children.",
  },
  HSSTransport: {
    strength:
      "Refers to family having a car, or regular access to a car or public transportation. Reliable transportation allows family to meet obligations such as doctors’ visits, school, or regular work attendance.",
    baseline:
      "Refers to family normally having regular access to reliable transportation. Occasionally, transportation difficulties will cause an issue for the family (e.g., arriving late to work because of difficulties).",
    challenge:
      "Refers to family not having transportation available which, in turn, inhibits continuity of work, increases social isolation, and/or limits access to services, and/or prevents regular school attendance. Caregivers cannot keep medical or service appointments reliably, and they cannot assure those services for children.",
  },
  HSSOverall: {
    strength:
      "Refers to family receiving very high ratings in the following areas: caregiver employment, family income, financial management, food and nutrition, and transportation.",
    baseline:
      "Refers to family receiving adequate ratings in all of these areas: caregiver employment, family income, financial management, food and nutrition, and transportation.",
    challenge:
      "Refers to family receiving very low ratings in the following areas: caregiver employment, family income, financial management, food and nutrition, and transportation.",
  },
  IFHPPhysical: {
    strength:
      "Caregivers enjoy excellent physical health. There are no health issues that interfere with parenting, employment, or participating in everyday life. Caregivers promote good health in family, including keeping watch over diet, exercise, and lifestyle habits of children and other family members.",
    baseline:
      "Caregivers enjoy good basic health. May have some health issues, such as elevated blood pressure or mild diabetes that are under control through medication and routine health care. Health issues may occasionally inhibit caregivers, but do not pose major obstacles in parenting abilities or significantly hinder the caregivers’ ability to parent. Caregivers are knowledgeable about health status and normally make lifestyle and diet choices accordingly.",
    challenge:
      "Caregivers suffer from one or more chronic debilitating physical health issues (such as serious obesity, high blood pressure, HIV/AIDS), or progressive diseases (such as cancer, AIDS, etc.) that significantly interfere with daily life. Caregivers do not understand implications of diet, lifestyle, exercise, or proper medication regimen, and therefore do not manage the health condition(s) to the extent possible. Caregivers project personal health issues on children or other household members and/or or they require children to provide physical care.",
  },
  IFHPDisability: {
    strength:
      "Caregivers do not suffer from any disabling conditions (blindness, physical handicap, chronic back pain, etc.) that would interfere with parenting, employment, or participating in daily life. Caregivers are physically fit, and activities are not limited or constrained.",
    baseline:
      "Caregivers may suffer from one or more mildly disabling conditions that do not prevent caregivers from parenting or working, although some accommodation may be necessary (e.g., no lifting, no driving, accessible workplace) and caregivers are able to participate in daily life with some accommodation to circumstances. If disabling condition qualifies for benefits, caregivers receive benefits and participate in benefit program requirements.",
    challenge:
      "Caregivers suffer from serious disabling condition (such as total blindness, para/ quadriplegia, absent limb, spastic colon, etc.) that substantially inhibits or prevents caregivers from effectively parenting children, working, or participating in everyday life. Caregivers may be SSI dependent. Caregivers are not able to manage disability and rely on others for daily living, and they may not make necessary lifestyle or medical decisions to assist others in managing the disability.",
  },
  IFHPMental: {
    strength:
      "Caregivers enjoy good mental health. No evidence of mental illness or emotional instability. Caregivers know how to handle stress, and how to interact with other family members in ways that promote good mental health and stability. Have good sense of humor. Caregivers may have had mental health or emotional issues in the past but have successfully resolved them and use personal mental health history to promote good future health.",
    baseline:
      "Caregivers may suffer from non-debilitating mental health issues, but have them under control either through treatment, medication, or counseling. Caregivers may be subject to mood swings or need to manage medications, but do so in such a way that parenting, employment, and daily life are not precluded or impaired. Mental health issues (mild depression) may occasionally inhibit caregivers, but do not significantly hinder the caregivers’ ability to parent.",
    challenge:
      "Caregivers suffer from serious and persistent mental health issues or are emotionally distraught. May be deeply depressed, actively psychotic, suicidal. May suffer from periods of mental or emotional impairment that prevent effective parenting, employment, or participation in daily life. Caregivers appear to be unwilling or unable to manage mental health issues, or medication regimen that would ameliorate symptoms. Family members or community may be at risk of harm from caregivers, or caregivers may be at risk of harm to self. Caregivers project personal mental health issues on children or other household members.",
  },
  IFHCPhysical: {
    strength:
      "Children enjoy excellent physical health. There are no health issues that interfere with normal development, education, or participating in everyday life. Children receive regular dental and physical health checkups and immunizations. Illnesses and injuries are dealt with properly and appropriately. Children promote good health as appropriate to age, and are mindful to watch diet, exercise, and to make healthy choices, as appropriate to age. All immunizations are up to date, and caregivers have immunization and other health records available.",
    baseline:
      "Children enjoy good basic health. May have some health issues, such as asthma or being slightly overweight for age, but medical conditions are under control through medication and routine health care, and efforts are in place to lead healthy life even if children are not always successful at managing their own health. Physical health issues do not inordinately adversely affect children participation in family life or school, etc. Children are knowledgeable about health status, as age-appropriate, and participate in health management. Caregivers believe that children’s immunizations are up to date, but health records may be incomplete.",
    challenge:
      "Children suffer from one or more chronic debilitating physical health issues (such as serious obesity, childhood diabetes, serious asthma or life-threatening allergies, HIV/AIDS), or progressive diseases (such as cancer, AIDS, etc.) that significantly hinder normal development orsocialization, and interfere with education and daily life. Children do not understand implications of diet, lifestyle, or exercise, or are not able to self-manage medication regimen, and do not participate in management of the health condition(s).",
  },
  IFHCDisability: {
    strength:
      "Children do not suffer from any disabling conditions (blindness, physical handicap, physical birth defect, developmental disability, etc.) that would interfere with normal development, education, or participating in daily life. Children are physically active and within normal height/weight for age.",
    baseline:
      "Children may suffer from one or more mildly disabling conditions that do not prevent education, socialization, or participation in daily life, although some accommodation may be necessary (accessibility accommodations at home and school) and caregivers are able to participate in daily life with some accommodation to circumstances. If disabling condition qualifies for benefits, caregivers receive benefits and participate in benefit program requirements.",
    challenge:
      "Children suffer from serious disabling conditions (such as blindness, physical deformity, cerebral palsy, para/quadriplegia, absent limb, moderate or severe developmental disability, etc.) that substantially inhibits or prevents children from participating in everyday life. Children may be SSI dependent. Children are not able to manage disability and rely on others for daily living. Daily life may require extraordinary machinery or attendant care that is not readily available or accessible by family.",
  },
  IFHCMental: {
    strength:
      "Children enjoy good mental health. No evidence of mental illness or emotional instability. Children know how to handle stresses encountered as part of normal development, and they know how to interact with other family members in ways that promote good mental health and stability. Have good sense of humor and good outlook on life and good self-image, as age-appropriate.",
    baseline:
      "Children may suffer from non-debilitating mental health issues, but have them under control either through treatment, medication, or counseling. Children may have had episodes of situational anxiety or mild depression associated with normal issues and experiences during various stages of development. Children may experience being bullied, stigmatized, or in other ways victimized by peers or others in environment, but they are reasonably successful in resolving these issues or circumstances, perhaps with caregivers’ help. Caregivers may need to assist children to manage medications, or in advocating for children with school or peers, and this happens in a way that promotes children’s mental health and stability.",
    challenge:
      "Children suffer from serious and persistent mental health issues or emotional disturbance. May be deeply depressed, actively psychotic, suicidal. May suffer from periods of mental or emotional impairment that adversely affect children’s socialization or participation in normal activities of daily life. Children may be stigmatized by peers or may suffer poor self-concept. Inability to handle stress. If diagnosed with mental illness, and/or other emotional disabilities, caregivers appear to be unwilling or unable to assist children to overcome or manage mental health issues, or medication regimen that would ameliorate symptoms. Caregivers cannot or will not advocate for children with school or peers to resolve issues, and they will not or cannot access appropriate treatment. Family members or community may be at risk of harm from children, or children may be at risk of harm to self.",
  },
  IFHAccess: {
    strength:
      "Family has access to prophylactic and palliative health and mental health care. Caregivers know how to access care, and where care is located. Insurance is available or family resources are sufficient that lack of money is not a reason to delay seeking medical or mental health assistance. Caregivers know family health and mental health history, have accurate records, and can assist health care system in provision of care. Good physical and mental health are valued by caregivers and other family members, and family supports each other’s receipt of health care when needed.",
    baseline:
      "Family has basic health care needs met through normally available healthcare outlets such as doctor offices or clinics. Family may not seek preventive care and may delay seeking palliative care unless injury is serious or illness is advanced. Insurance or re-sources may be insufficient to cover full cost of health care, but family members go to doctor when necessary and try to pay for treatment received.",
    challenge:
      "Family has no insurance, children have only Medicaid or have no insurance. Family relies on emergency room care for serious injuries or serious illness, but does not seek normal preventive or palliative care. Caregivers do not know where or how to access healthcare or mental health care, and may rely on ineffective alternative treatments to the detriment of child or other family members. Lack of healthcare or mental health care treatment poses a threat to family continuity and safety.",
  },
  IFHOverall: {
    strength:
      "Refers to family receiving very high ratings in the following areas: caregiver physical health, caregiver disability, caregiver mental health, child physical health, child disability, child mental health, family access to health and mental health care.",
    baseline:
      "Refers to family receiving adequate ratings in all of the areas: caregiver physical health, caregiver disability, caregiver mental health, child physical health, child disability, child mental health, family access to health and mental health care.",
    challenge:
      "Refers to family receiving very low ratings in the following areas: caregiver physical health, caregiver disability, caregiver mental health, child physical health, child disability, child mental health, family access to health and mental health care.",
  },
  JCCAParent: {
    strength:
      "Caregiver responds appropriately to child, both verbally and non-verbally. Caregiver receptive and responsive to services designed to support reunification by bringing the caregiver and child closer together; is willing to attend to child’s needs before their own. Caregiver acknowledges and accepts responsibility for role in family difficulties leading to removal.",
    baseline:
      "Caregiver generally responds appropriately to child but may harbor some resentment or occasional feeling of intrusion or excessive demands by child. Caregiver accepts some responsibility for family difficulties leading to removal and is making progress in this area. Generally positive feelings towards child but may need ongoing support or additional services after reunification.",
    challenge:
      "Caregiver purposefully abused/neglected child in the past; expresses disaffection towards child; associates negative feelings towards child with negative feelings towards child’s other caregiver; originally requested removal of child. Caregiver claims not to understand child, fails to respond to child appropriately or responds very inappropriately; expresses disillusionment with child, feels anger or a sense of violence towards child, and/or resents child’s interference with caregiver’s own life. Caregiver blames child for family difficulties leading up to removal; caregiver has refused to respond to services intended to achieve reunification.",
  },
  JCCAChild: {
    strength:
      "Child is very comfortable in presence of caregiver; expresses love for caregiver. Child responds appropriately to caregiver affect, expressions of love, and exercising of caregiver responsibilities (limit setting, discipline). As appropriate to age, child exhibits a desire to live with caregiver; acknowledges and accepts any responsibility child had for family difficulties leading to removal; has responded to and engaged in treatment or services intended to effect reunification.",
    baseline:
      "Child is generally comfortable in caregiver’s presence but may respond fearfully or withdraw if caregiver becomes angry or if family tensions arise. Child generally responds appropriately to caregiver affect but may resist caregiver limit setting or discipline. Does not always acknowledge caregiver authority or responsibility. Child expresses a desire to be with caregiver but expresses some reservations about caregiver’s desire to be with child. Child is somewhat conflicted by desire to return home, leaving behind feelings of security or comfort afforded during period of substitute care.",
    challenge:
      "Child is fearful of caregiver. Child experienced serious physical or emotional harm prior to removal and holds caregiver responsible (verbally or behaviorally). As appropriate to age, child verbally or otherwise expresses fear, mistrust, anger or feelings of violence towards caregiver, feelsthat caregivers limits are too strict, is embarrassed by caregiver; states that he/she will not stay with caregiver to work out issues that may arise in the future.",
  },
  JCCASub: {
    strength:
      "Substitute care provider has always expected reunification to occur; supports reunification philosophically; is willing to work with and/or be a resource for caregiver to achieve successful reunification.",
    baseline:
      "Substitute care provider understands policy on reunification but has bonded with child and is experiencing feelings of separation/loss. As age appropriate, child feels or behaves with reciprocity. Substitute care provider expresses some reservations about caregiver’s ability to adequately care for child, but provider is willing to give caregiver a fair chance at resuming role as caregiver.",
    challenge:
      "Substitute care provider opposes reunification; threatens or has taken legal steps to block reunification; strongly views caregiver as flawed or unworthy of return of child; has pro-vided/promoted different socio-economic environment (e.g., food, clothing, play items, recreation) for child and uses that difference to sabotage reunification efforts. Substitute care provider has denigrated caregiver to child, actively expressed view of caregiver’s inability to resume parental/caregiver role.",
  },
  JCCAAttachment: {
    strength:
      "Both caregiver and child long to resume intimate family relationship. As age appropriate, each acknowledges the strengths and limitations of the other, and is willing/eager to resume relationship with accommodation to limitations.",
    baseline:
      "Child and/or caregiver acknowledge that separation has been painful and have worked/ are working to repair relationship. Relationship reparations includes counseling or other treatment regarding development and age-appropriate expectations, to effect reconnection between child and caregiver.",
    challenge:
      "Child or caregiver or both express marked feelings of lost attachment to the other. Period of separation has been very long, and/or child was removed at very young age and has developed/aged/bonded in relation to persons other than the caregiver. Child is at a markedly different stage of development (particularly for older children) than when removal from caregiver occurred. Caregiver may have had another child or joined another family unit and has transferred affection/attention to other persons.",
  },
  JCCAVisits: {
    strength:
      "Both caregiver and child (as age appropriate) exhibit positive anticipation of home visits. Caregiver plans activities or special time together with child and executes those plans. Home visitations have progressed well in terms of increasing frequency and duration and decreasing necessary supervision. Visitations are incident-free; child and caregiver ex-press sadness/sorrow that visitation period ends.",
    baseline:
      "Caregiver and child (as age appropriate) are working out issues and re-establishing roles during home visitations. Some minor incidents may arise, but caregiver discusses them with service provider and uses them as opportunity to learn more and prepare for next visit. Child and/or caregiver complete visits with minor reservations about longer term reunification, but they continue to work to resolve differences or issues.",
    challenge:
      "Caregiver has not participated satisfactorily in scheduled home visits; has missed visits, failed to supervise child adequately during visits; has requested early termination of vis-its, has allowed family issues to escalate into incidents of high tension or even suspected abuse/neglect of child. Child has requested early termination of visits, has refused to stay with caregiver, has reported maltreatment (substantiated or not) by caregiver during visitations.",
  },
  JCCAOverall: {
    strength:
      "Both child(ren) and caregiver(s) are eager to reunite, as evidenced by both verbal and behavioral expression of desire to be together. Family receives mild to clear strength rating on the items in this domain: Parent/Caregiver Ambivalence Towards Child, Child Ambivalence Towards Parent/Caregiver, Ambivalence Exhibited by Substitute Care Provider, Disrupted Attachment, Pre-Reunification Home Visitations.",
    baseline:
      "Both child(ren) and caregiver(s) say they want to be together; one or both may be apprehensive or nervous about reunion, but that apprehension is determined to be due to uncertainty about capability rather than competition for affection, substantive inability to parent, or significant unresolved treatment issues. Some mild issues may be present on the items comprising this domain, but family is working to resolve those issues.",
    challenge:
      "Child(ren) and/or caregiver(s) express serious reservations about being together, either due to fear of future harm, strong negative affect by one or both parties towards the other, previous history of removal of this or other child(ren), and or prior failed reunification efforts. Generally challenging or some strongly negative ratings on the items comprising this domain: Parent/Caregiver Ambivalence Towards Child, Child Ambivalence Towards Parent/Caregiver, Ambivalence Exhibited by Substitute Care Provider, Disrupted Attachment, Pre-Reunification Home Visitations.",
  },
  KRRCPSRisk: {
    strength:
      "Caregiver has addressed needs of family (transportation, housing, employment, income, supervision, etc). If appropriate, perpetrator has been removed from family by remaining caregiver. Caregiver has reconstructed living environment to afford protection and care of child.",
    baseline:
      "Caregiver has made substantial progress towards resolution of risk factors that led to removal. Some issues remain unresolved, but improved, and progress continues to be made. Caregiver acknowledges and accepts responsibility for continued work on those issues.",
    challenge:
      "Caregiver has maintained destructive, abusive, or inappropriate relationships with other adults (or perpetrator) or has established new such relationship(s) in child absence. Care-giver has failed to address needs that place family under extreme stress or threat of legal intervention such as continued use of drugs, alcohol, or engaging in prostitution, or criminal lifestyle, etc.",
  },
  KRRPlans: {
    strength:
      "Caregiver has successfully completed required services and/or voluntary services (esp. alcohol/drug abuse, anger management, crisis management, communications) and has demonstrated newly acquired skills/abilities. Caregiver appears gratified by new skills/ abilities and appears to have internalized change. Caregiver is approachable and receptive to the idea of ongoing services and is eager or willing to participate.",
    baseline:
      "Caregiver has completed required services at least to the extent required by court order or authoritative service plan. Caregiver can verbalize knowledge about skills/behaviors/ abilities but has not necessarily demonstrated them. Caregiver may deny having needed some of the offered services but acknowledges benefits of some of the services.",
    challenge:
      "Caregiver strongly denies need forservices, is oppositional to receipt ofservices, has failed to participate meaningfully or complete required services. Caregiver repeatedly exhibits behaviors that were the focus ofservice plan, and/or flaunts non-compliance to service providers or others in authority. Caregiver blames others, including service providers, the system, adult partner(s) or child for issues and in defense of non-participation.",
  },
  KRRLegal: {
    strength:
      "Caregiver has pursued legal remedies or accepted legal services to resolve specific issues of a legal nature, including obtaining domestic violence restraining order, resolving legal charges resulting from abuse/neglect allegations. If appropriate, paternity has been established and child support is being provided. Other legal/criminal difficulties being experienced by caregiver that may affect future ability to parent or provide care have been resolved (e.g. pending eviction, criminal court cases).",
    baseline:
      "Caregiver is engaged in process of resolving legal issues that may affect ability to provide steady competent care. Some issues are still not completely resolved, but caregiver is engaged in the process of resolution, with appropriate and realistic expectations. Caregiver is not resistant to receiving legal assistance when it is necessary. None of the unresolved issues is likely to cause family chaos or child(ren)’s removal.",
    challenge:
      "In spite of restraining order, caregiver continues to maintain destructive or dangerous relationship with other adult(s); caregiver has chosen to legally oppose authority in spite of low probability of winning and is expending energy on losing legal conflicts rather than expending energy on becoming a more competent, caring caregiver. Caregiver has serious legal charges pending that may result in incarceration or other serious impediment to future care-giving.",
  },
  KRRNeeds: {
    strength:
      "Caregiver has completed education or counseling on child-centered issues and understands what to expect from child, as age appropriate, including possible different developmental stage of child than prior to removal (e.g., puberty). If child has cognitive or developmental disabilities, caregiver knows what to expect and has service plans in place to help child develop. If child is coming out of institution or closed treatment facility, caregiver is aware of and supportive of treatment goals and is prepared to support future treatment.",
    baseline:
      "Caregiver is fairly knowledgeable of treatment provided to child during period of out-of-home care and seems to understand treatment goals. Caregiver may not understand completely the potential future treatment needs of child but professes to support future treatment if needed. Caregiver may not fully understand cognitive/developmental dis-abilities of child, but is willing to accept outside assistance, if needed. Caregiver may not fully understand medication regimen but is willing to administer medication and to allow child to have access to ongoing psychological services.",
    challenge:
      "Caregiver blames child for cognitive or developmental disabilities, holds child responsible for progress that may not be attainable. Caregiver views normal child developmental processes as deliberately oppositional to caregiver authority or as lack of respect. Caregiver unwilling to engage in discussion of child’s experiences in institutional or closed facility care and expects child to return fixed or cured. Caregiver denies need for or opposes medications or ongoing psychological or medical services.",
  },
  KRRBackupPlans: {
    strength:
      "Caregiver has established plans and back-up plans for supervision of child, for accessing emergency family services, for respite if needed. Caregiver has plans for accessing social services, mental health services or law enforcement, if needed. Friends and family are available for social/emotional support. Plans and mechanisms are in place to provide health care, education, and age-appropriate socialization of child.",
    baseline:
      "Caregiver has some plans in place to provide basic supervision of child or to access ser-vices on emergency basis. Caregiver has minimum social/family support. Caregiver does not oppose health, education or socialization efforts on child behalf, but is fairly passive, relying on external sources such as school system and emergency medical care.",
    challenge:
      "Caregiver has constructed no plans or ineffective plans for childcare and supervision. Caregiver opposes outside provision of services (e.g., refuses to respond to requests by school for parent meeting). Caregiver remains oppositional with regard to publicly pro-vided social services, mental health services, psychological services and has not engaged those services for post-reunification support. Caregiver remains estranged from family and/or remains socially isolated and therefore without social or emotional support should a crisis or need for assistance arise.",
  },
  KRROverall: {
    strength:
      "Family has made substantial progress on practical/logistical/legal issues since removal and is ready to have child returned permanently. Family generally receives mild- to clear-strength ratings on the items associated with this domain: Resolution of Significant CPS Risk Factors, Completion of Case Service Plans, Resolution of Legal Issues, Parent/Care-giver Understanding of Child Treatment Needs, Established Back-Up Supports and/or Service Plans.",
    baseline:
      "Family has made some progress on practical/logistical/legal issues and is moving in the right direction. Some issues may remain, but they are not viewed as sufficiently serious to prevent reunification. Additional services may be necessary to continue progress on outstanding or unresolved issues. Some mild challenge ratings may be evident, but family is making progress on those items.",
    challenge:
      "Family clearly not ready for return of child due to family chaos, unsatisfactory or high-risk living situations or dangerous or illegal family lifestyles. Little or no progress made on the issues leading to removal. Family embroiled in contests with the law and with authority figures in general. Family receives numerous challenge ratings in this domain.",
  },
  LTSexual: {
    strength:
      "Refers to no evidence that traumatic sexual abuse has occurred. Children have strong relationships with trusting and trustworthy adults who provide a safe environment and ongoing protection of the children. These adults assure that, as appropriate to age and development, children are educated about risks of sexual abuse, assault, molestation, and know how to avoid or defend against their occurrence.",
    baseline:
      "Refers to no evidence that traumatic sexual abuse has occurred. Or traumatic sexual abuse has occurred in the past, appropriate services are being or have been received, and satisfactory healing is evident. As appropriate to age and development, children have a basic understanding about risks or safety/self-protection measures relating to sexual abuse.",
    challenge:
      "Refers to traumatic child sexual abuse (inappropriate contact/fondling, sexual activities; vaginal, anal, oral penetration/intercourse) or molestation having definitely occurred. Seriousness of the trauma is affected by the emotional/relational closeness of the perpetrator (person known to family, formerly trusted person, family member); frequency of abuse; duration of abuse; parent/caretaker reactions to disclosure (blaming, assigning blame, immediate protective actions, consistency of reactions).",
  },
  LTPhysical: {
    strength:
      "Refers to no evidence that traumatic physical abuse has occurred. Children live in a safe, stable, supportive environment and have strong relationships with trusting and trustworthy adults.",
    baseline:
      "Refers to no evidence that traumatic physical abuse has occurred. Or traumatic physical abuse has occurred in the past, appropriate services are being or have been received, and satisfactory healing is evident. In consideration of age, children have a basic understanding about risks or safety/self-protection measures relating to physical abuse.",
    challenge:
      "Refers to traumatic physical abuse having definitely occurred as a result of excessive/ inappropriate/cruel discipline, family conflict escalating to violence. Seriousness of the trauma is related to the abuse being repetitive abuse; sadistic/ritualistic abuse; physical abuse accompanied by other forms of abuse (emotional, sexual); abuse resulting in physical injury; seriousness of injury. Incidents of medical trauma (serious injury followed by hospitalization, surgery, medical confinement, exposure to life threatening trauma, extended loss of function such as vision/hearing/mobility, or disfigurement) can also result in serious and lasting post-traumatic symptoms.",
  },
  LTNeglect: {
    strength:
      "Refers to no evidence that traumatic neglect has occurred. Children live in a safe environment. Children’s physical, emotional, medical, nutritional, and safety needs are met. As appropriate to age and development, children have supervision, resources, family support, and strong relationships with trusting and trustworthy adults.",
    baseline:
      "Refers to no evidence that traumatic neglect has occurred and children are at least minimally supervised (taking into consideration children’s age and capabilities). Or traumatic neglect has occurred in the past, appropriate services are being or have been received, and satisfactory healing is evident. Children have a basic understanding about risks or safety/self-protection measures in the absence of competent adult supervision.",
    challenge:
      "Refers to traumatic neglect having definitely occurred, resulting from serious lack of supervision. Children exhibit a sense of chronic insecurity due to absence of food, shelter, clothing; and absence of caring adult providing safety, comfort, and basic necessities that create a sense of well-being. Traumatic neglect may lead to situations where necessary medical treatment is not provided causing unnecessary suffering and possible long-term health deficits. Regular school attendance or academic engagement is disrupted, accompanied by school/community detachment and consequential social/developmental deficits. Children engage in high-risk, unsupervised activities, and lack of behavioral risk management is likely to result in or has resulted in injury.",
  },
  LTEmotional: {
    strength:
      "Refers to no evidence that traumatic emotional/psychological trauma has occurred. Children’s emotional and developmental needs are being met. Children live in a safe, stable, supportive environment and have strong relationships with trusting and trustworthy adults.",
    baseline:
      "Refers to no evidence that traumatic emotional/psychological trauma has occurred. Or traumatic emotional/psychological trauma has occurred in the past, appropriate services are being or have been received, and satisfactory healing is evident. As appropriate to age and development, children have a basic understanding about threats to emotional/psychological health and well-being.",
    challenge:
      "Refers to emotional/psychological trauma having definitely occurred. Emotional Trauma: Children have experienced denigration, humiliation; witnessing intra-familial violence, especially if repetitive, serious, with injury resulting, and if accompanied by a perceived inability to protect self or others. Children have experienced unexpected disruptions of environment and family stability, including eviction, homelessness, forced removal from parent/caregiver, and placement into shelter care, foster care, or institutional care. Psychological Trauma: Children have witnessed community violence: violence committed by others on others, especially if resulting in serious injury or death. Witnessing significant criminal activity, especially if within family, with subsequent disruption of perceived security. Children have been removed from home by child protection authorities without comprehending the reasons for the removal, particularly if the removal is overtly contentious. Children have experienced a large-scale natural disaster (hurricane, tornado, earthquake, flood) accompanied by loss of secure environment, loss of material possessions; endured a harrowing survival experience, especially if accompanied by injury, loss of family/loved ones/pets.",
  },
  LTTrauma: {
    strength:
      "Refers to caregiver not having experienced trauma but being aware of symptoms of trauma and actively avoiding situations, circumstances, relationships likely to be associated with traumatic events. Or caregiver has experienced trauma and has successfully resolved and healed from trauma.",
    baseline:
      "Refers to caregiver not having experienced trauma and caregiver is minimally aware of symptoms of trauma. Or caregiver has experienced trauma, understands the negative consequences of trauma on the caregiver personally and on caregiver’s ability to parent, and is receiving services to resolve the trauma. Progress is satisfactory at the time of assessment",
    challenge:
      "Refers to caregiver having definitely experienced trauma (possibly including abuse/neglect as a child), and not having addressed the trauma, received services, healed, or resolved the trauma. Caregiver exhibits trauma-related symptoms, including depression, fear, anxiety, suicidal thoughts or gestures, poor physical health, destructive behavior, criminality, incarceration, substance abuse, domestic violence.",
  },
  LTOverall: {
    strength:
      "Refers to family receiving very high ratings in the following areas: traumatic sexual abuse, traumatic physical abuse, traumatic neglect, traumatic emotional/psychological abuse, parent/caregiver trauma.",
    baseline:
      "Refers to family experiencing no more than a few minimal challenges in the following areas: traumatic sexual abuse, traumatic physical abuse, traumatic neglect, traumatic emotional/psychological abuse, parent/caregiver trauma",
    challenge:
      "Refers to family experiencing multiple or challenges in the areas of traumatic sexual abuse, traumatic physical abuse, traumatic neglect, traumatic emotional/psychological abuse, parent/caregiver trauma.",
  },
  MPTPhysical: {
    strength:
      "Refers to children meeting normative standards for growth and development, including fine and gross motor skills. Temperament is normative. Overall health is good, and screening results are positive and normative. Body-mass index (BMI) is normative. Academic skills (literacy/numeracy) and problem-solving skills are developing appropriate for age of child, identity development is normative, risk-avoidance behavior is evident, as well as school engagement and attendance; emergence of problem-solving skills and decision-making skills. Cognitive and health assets are within normative standards.",
    baseline:
      "Refers to children exhibiting cognitive and/or physical post-trauma symptoms in relation to age that are associated with histories of traumatic abuse/neglect, but they are receiving services to ameliorate the effects of the trauma, and their status is reasonable at the time of assessment. Overall, children and family are making progress and are engaged in the healing process",
    challenge:
      "Refers to children exhibiting cognitive and/or physical symptoms associated with traumatic abuse/neglect such as delayed language development, delayed motor skill development, generally degraded or delayed cognitive development. Screenings reveal physical/developmental/health deficits. Affect may appear flat, numb, non-engaging, with excessive excitability or avoidance. Older children exhibit poor academic or school engagement or progress. Somatic disorders and eating disorders result in abnormal BMI. Identity development suffers, children may identify with fringe elements or identities, and may engage in risky behaviors with potential long-term health/mental health consequences.",
  },
  MPTEmotional: {
    strength:
      "Refers to children exhibiting emergence of self-control and emotional management and expression. In consideration of age, children display appropriate internalization and externalization with respect to behavior. Children exhibit age-appropriate emotional intelligence, self-efficacy and self-control, pro-social behavior, positive outlook, coping skills.",
    baseline:
      "Refers to children exhibiting emotional/psychological post-trauma symptoms in relation to age but they are receiving services to ameliorate the effects of the traumatic stress, and their status is reasonable at the time of assessment. Overall, children and family are making progress and are engaged in the healing process",
    challenge:
      "Refers to children exhibiting emotional/psychological deficits such as poor self-control and/or emotion management leading to confrontation with caregivers/peers/persons in authority, and/or behaviors inappropriate to setting and context. Children exhibit poor or unrealistic self-concept, engage in anti-social behavior, and are pessimistic about the future and future possibilities of success and happiness. Children fail to deal with adversity or to positively resolve conflicts, become excessively confrontational or avoidant, sullen, withdrawn. Children exhibit inappropriate affect, clinical depression, or other serious emotional or mental health symptoms.",
  },
  MPTSocial: {
    strength:
      "Refers to children exhibiting the normal emergence of normative social competencies, attachment and caregiver relationships, and adaptive behavior. As appropriate to age, children show peer socialization and development of friendships, social connections and social skills, acceptance of racial/cultural/ethnic diversity. Children successfully integrate into school, community, and other normative settings.",
    baseline:
      "Refers to children exhibiting social deficits associated with post-trauma symptoms in relation to age, but they are receiving services to ameliorate the effects of the trauma, and their status is reasonable at the time of assessment. Overall, children and family are making progress and are engaged in the healing process.",
    challenge:
      "Refers to children exhibiting maladaptive socialization such as failing to bond with caregiver, displaying hyper-vigilance, viewing everyday environment as fraught with threats and danger, reacting to most or all stimuli as threatening, exhibiting fear. Children fail to form meaningful relationships or friendships, particularly with adults, are suspicious of others, and may become rejecting of authority. Children may denigrate persons of different racial or ethnic backgrounds, may engage in racist activities, may reject or otherwise fail to integrate into community, school, or other normative social environments, may align with fringe groups or identities, and may be attracted to criminal activities and lifestyles.",
  },
  MPTSupport: {
    strength:
      "Refers to caregiver providing environmental buffers to traumatic stress (stable home life, predictable routines, and stable interpersonal relationships) and other protective factors and resources (food, nutrition, supervision, basic necessities). Caregiver advocates for children, understands trauma symptoms, and knows how to help the children heal and recover. Caregiver acknowledges the traumatic events and places blame and responsibility on the source, not on the children. Caregiver is understanding and tolerant of children’s needs with respect to post-trauma healing, is patient, empathetic, and participates in healing services and strategies.",
    baseline:
      "Refers to caregiver acknowledging traumatic events and seeks to help children recover, even if caregiver was complicit (by omission or commission) in the traumatic events affecting the children. Caregiver accepts responsibility for any contribution to occurrence of traumatic events. Caregiver is able to maintain a basic semblance of family life. Caregiver may require assistance in maintaining treatment services to address children’s needs but is willing to do so",
    challenge:
      "Refers to caregiver being unable to provide safe, stable nurturing environment needed by children to assist in recovery. Caregiver is unable or unwilling to acknowledge occurrence of children’s trauma. Caregiver blames child for incurring trauma. Caregiver’s personal issues constantly and seriously interfere with ability to maintain family life. These issues appear to overwhelm the caregiver and place the children at risk of child abuse, neglect, or trauma, likely leading to child removal and placement into substitute care.",
  },
  MPTParents: {
    strength:
      "Refers to ability to meet the family’s needs (employment, housing, and food),physical and mental health, interpersonal relationships, feeling part of thecommunity, satisfaction with life, and utilization of services. Caregiver has astrong bond with children, displays loving, sensitive childcare, and the childrenreciprocate with a strong attachment. If caregiver has a personal history oftrauma, caregiver uses that history and recovery to help protect children fromexperiencing trauma.",
    baseline:
      "Refers to caregiver requiring assistance in maintaining treatment services to address caregiver’s own post-trauma needs and is willing to do so. Caregiver does not allow personal issues to inordinately interfere with family life",
    challenge:
      "Refers to caregiver having own unresolved history of trauma (possibly including abuse/neglect as a child) inhibiting caregiver from engaging in assessment, service planning, or service delivery/receipt. Caregiver has co-occurring mental health/mental illness, substance abuse, and/or unstable personal relationships that create additional risks or inhibit provision of stable environment. Caregiver’s issues interfere with labor-force attachment or stable employment, family is under-resourced and threatened with poverty-related deficits and stressors. Caregiver maintains destructive relationships that are likely to result in divorce, separation, domestic violence, and other family violence; unwanted pregnancy; and abandonment.",
  },
  MPTOverall: {
    strength:
      "Refers to family receiving very high ratings in the following areas: post-traumatic cognitive and physical well-being, post-traumatic emotional/psychological well-being, post-traumatic social functioning, post-traumatic parent/caregiver support of child, parent/caregiver well-being.",
    baseline:
      "Refers to family experiencing a few minimal challenges in the following areas: post-traumatic cognitive and physical well-being, post-traumatic emotional/psychological well-being, post-traumatic social functioning, post-traumatic parent/caregiver support of child, parent/caregiver well-being",
    challenge:
      "Refers to family experiencing multiple or challenges in the areas of post-traumatic cognitive and physical well-being, post-traumatic emotional/psychological well-being, post-traumatic social functioning, post-traumatic parent/caregiver support of child, parent/caregiver well-being.",
  },
};

function SAFYNCFAS() {
  const location = useLocation();
  const navigate = useNavigate();
  const initialProgram = location.state?.selectedProgram;

  //Get route and query string params
  const { clientid, formid, submissionid } = useParams();

  //Get logged in user info
  const safyInfo = JSON.parse(localStorage.getItem("safyInfo"));
  const [loadStatus, setLoadStatus] = useState({});
  //The complete Form Submission object with form data
  const [formsyDetails, setFormsyDetails] = useState({});
  const [formsyConfig, setFormsyConfig] = useState({});
  //The form data and previous forms
  const [data, setData] = useState({});
  const [allFormSubmissions, setAllFormSubmissions] = useState([]);
  //Touched fields
  const [touched, setTouched] = useState({});
  const [isSaveInProgress, setIsSaveInProgress] = useState(false);
  //Errors
  const [errors, setErrors] = useState({});
  //Client, Program and CareTeam Info
  const [clientData, setClientData] = useState({});
  const [careTeams, setCareTeams] = useState([]);

  //Signatures State Property
  const [signatures, setSignatures] = useState([]);
  //Show Signature Modal
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  //Comments state Property
  const [comments, setComments] = useState();
  //Show Comments Modal
  const [showComment, setShowComment] = useState(false);
  //Show Share Modal
  const [showShare, setShowShare] = useState(false);
  //Show Form Level (not field specific) Comments
  const [showFormComment, setShowFormComment] = useState(false);

  const [doCompare, setDoCompare] = useState(false);
  const [timeoutId, setTimeoutId] = useState();

  const pdfRef = useRef(true);
  const isMountedRef = useRef(true);

  //Get validation schema
  const schema = getSchema(formid);

  const formDisabled = useMemo(() => {
    return formsyDetails.Active === "0" || formsyDetails.FinalSigned === "1";
  }, [formsyDetails]);

  const formConfig = useMemo(() => {
    return formsyConfig.FormConfiguration?.[0].config[0] || {};
  }, [formsyConfig]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      pdfRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (initialProgram?.ccx_client_programid && loadStatus.dataSet) {
      setData((d) => {
        const obj = { ...d };

        if (
          obj.SelectedProgram === undefined ||
          getFieldValue(obj.SelectedProgram)
        ) {
          return obj;
        }

        obj.SelectedProgram["@value"] = initialProgram.ccx_client_programid;
        return obj;
      });

      setFormsyDetails((fd) => {
        const details = { ...fd };

        if (details.ClientProgramId === initialProgram.ccx_client_programid) {
          return details;
        }

        details.ClientProgramId = initialProgram.ccx_client_programid;
        details.ClientProgramName = getFieldName(
          initialProgram.ccx_programunit
        );
        return details;
      });
    }
  }, [initialProgram, loadStatus.dataSet]);

  //Get Client Info
  useEffect(() => {
    if (!formid || !clientid) {
      return;
    }

    const loadClientInfo = async () => {
      const response = await Promise.all([
        getFormsySubmissionsForClient(formid, clientid),
        getCareTeams(clientid),
      ]);

      if (!isMountedRef.current || !response) {
        return;
      }

      setAllFormSubmissions(response[0]);
      setCareTeams(response[1]);
    };

    loadClientInfo();

    if (submissionid !== undefined) {
      const loadSubmission = async () => {
        const response = await getSubmission(submissionid);

        if (!isMountedRef.current || !response) {
          return;
        }

        setData(response.Data);
        setFormsyDetails(response.FormsyDetails);

        if (response.Comments) {
          setComments(response.Comments);
        }

        if (response.Signatures) {
          setSignatures(response.Signatures);
        }

        setLoadStatus((ls) => {
          return { ...ls, dataSet: true };
        });
      };

      loadSubmission();
    }
  }, [formid, clientid, submissionid]);

  useEffect(() => {
    if (
      !formid ||
      !clientid ||
      !safyInfo?.user ||
      loadStatus.configLoaded ||
      loadStatus.loadingConfig
    ) {
      return;
    }

    const loadConfig = async () => {
      setLoadStatus((ls) => {
        return { ...ls, loadingConfig: true };
      });
      const response = await getConfig(formid);

      if (!isMountedRef.current || !response) {
        return;
      }

      setFormsyConfig(response);
      const clientDataResponse = await getClientData(
        clientid,
        submissionid !== undefined,
        `${response.FormName}${
          response.VersionIntId > 1 ? `v${response.VersionIntId}` : ""
        }`
      );

      if (!isMountedRef.current || !clientDataResponse) {
        return;
      }

      setClientData(clientDataResponse);

      if (!submissionid) {
        //Set Formsy Details
        const newForm = {
          SubmissionId: "",
          FormId: response.FormId,
          FormVersionId: formid,
          ContactId: clientid,
          ContactName: clientDataResponse.client.fullName,
          ClientProgramId: "",
          ClientProgramName: "",
          TertiaryObjectId: "",
          TertiaryObjectName: "",
          FormResults: {
            results: {},
          },

          Comments: "",
          Signatures: "",
          CreatedOn: "",
          CreatedById: safyInfo.user.azureId,
          CreatedByName: safyInfo.user.displayName,
          ModifiedById: safyInfo.user.azureId,
          ModifiedByName: safyInfo.user.displayName,
          FinalSigned: "0",
          FinalSignedDate: "",
          Active: "1",
        };
        setFormsyDetails(newForm);
        setData(response.FormConfiguration[0].config[0]);
        setLoadStatus((ls) => {
          return {
            ...ls,
            loadingConfig: false,
            configLoaded: true,
            dataSet: true,
          };
        });
      } else {
        setLoadStatus((ls) => {
          return { ...ls, loadingConfig: false, configLoaded: true };
        });
      }
    };

    loadConfig();
  }, [
    formid,
    clientid,
    safyInfo,
    submissionid,
    loadStatus.configLoaded,
    loadStatus.loadingConfig,
  ]);

  const helperConfiguration = useMemo(() => {
    const defaultConfig = {
      buttonBarConfig: {
        sign: {
          display: false,
          initialSignatureType: "full",
        },
        clone: {
          display: false,
        },
        delete: {
          display: false,
        },
        share: {
          display: false,
        },
        comment: {
          display: false,
        },
      },
      signatureType: "full",
      finalSignType: "Provider",
    };
    if (!formsyConfig.HelperConfiguration?.[0]) {
      return defaultConfig;
    }
    try {
      return JSON.parse(formsyConfig.HelperConfiguration[0]);
    } catch {
      console.log(
        "helperConfiguration JSON:",
        formsyConfig.HelperConfiguration[0]
      );
      return defaultConfig;
    }
  }, [formsyConfig.HelperConfiguration]);

  const getSelectedProgramName = (dataObj, cData) => {
    if (!dataObj.SelectedProgram || !cData.programs) {
      return "";
    }

    let name = getFieldName(dataObj.SelectedProgram);
    if (name) {
      return name;
    } else {
      const selectedProgram = cData.programs.filter(
        (p) => p.ccx_client_programid === getFieldValue(dataObj.SelectedProgram)
      )[0];

      if (selectedProgram) {
        name = getFieldName(selectedProgram.ccx_programunit);
        const programDates = ` (${getFieldDate(
          selectedProgram.ccx_admitdate
        )} - ${
          selectedProgram.ccx_dischargedate
            ? getFieldDate(selectedProgram.ccx_dischargedate)
            : "no date"
        })`;
        return name + programDates;
      } else {
        return "";
      }
    }
  };

  //Form validation
  const validate = () => {
    const results = validateForm(data, schema);
    setErrors(results);
    return results;
  };

  const handleChange = ({ currentTarget: input }) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    const result = handleFormChange(input, data, schema, errors);
    setData(result.Data);
    setErrors(result.Errors);

    if (!submissionid) {
      return result.Data;
    }

    const touchedObj = { ...touched, [input.name]: true };
    const touchedFields = Object.keys(touchedObj).filter(
      (field) => touchedObj[field]
    );

    if (touchedFields.length >= 5) {
      if (isSaveInProgress) {
        const toId = setTimeout(() => {
          touchedFields.forEach((field) => (touchedObj[field] = false));
          handleSubmit(result.Data, null, null, null, true);
        }, 20000);

        setTimeoutId(toId);
      } else {
        clearTimeout(timeoutId);
        handleSubmit(result.Data, null, null, null, true);
        touchedFields.forEach((field) => (touchedObj[field] = false));
      }
    } else {
      const toId = setTimeout(() => {
        if (isSaveInProgress) {
          const toId2 = setTimeout(() => {
            touchedFields.forEach((field) => (touchedObj[field] = false));
            handleSubmit(result.Data, null, null, null, true);
          }, 20000);

          setTimeoutId(toId2);
        } else {
          touchedFields.forEach((field) => (touchedObj[field] = false));
          handleSubmit(result.Data, null, null, null, true);
        }
      }, 20000);

      setTimeoutId(toId);
    }

    setTouched(touchedObj);
    return result.Data;
  };

  ////SAVE
  const handleSubmit = async (
    dataOverride,
    formsyDetailsOverride,
    commentsOverride,
    signaturesOverride,
    autoSave = false
  ) => {
    if (isSaveInProgress) {
      return;
    }

    const dataObj = dataOverride || data;
    const formsyDetailsObj = formsyDetailsOverride || formsyDetails;
    const commentsObj =
      commentsOverride === undefined ? undefined : commentsOverride || comments; // commentsOverride is usually null, but specifically undefined during Final Signing
    const signaturesObj = signaturesOverride || signatures;

    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    setTouched({});
    const errs = { ...errors };

    if (!dataObj.SelectedProgram?.["@value"]) {
      errs.SelectedProgram = '"Selected Program" is required';
      setErrors(errs);
      return;
    }

    delete errs.SelectedProgram;
    setErrors(errs);
    setIsSaveInProgress(true);

    try {
      const submissionResponse = await saveForm(
        dataObj,
        formsyDetailsObj,
        commentsObj,
        signaturesObj,
        navigate,
        location,
        autoSave
      );

      if (!isMountedRef.current || !submissionResponse?.Id) {
        return;
      }

      const newDetails = { ...formsyDetailsObj };
      newDetails.ModifiedOn = submissionResponse.LastModifiedOn;
      newDetails.ModifiedById = safyInfo.user.azureId;
      newDetails.ModifiedByName = safyInfo.user.displayName;

      if (newDetails.SubmissionId === "") {
        newDetails.SubmissionId = submissionResponse?.Id;
      }

      setFormsyDetails(newDetails);
    } finally {
      setIsSaveInProgress(false);
    }
  };

  ////CLONE
  const handleOnClone = (event) => {
    event.preventDefault();
    cloneForm(
      data,
      formsyDetails,
      comments,
      signatures,
      navigate,
      location,
      formsyConfig
    );
    setSignatures([]);
    setExpandStatus(true);
    setShowDefinitions(true);
  };

  ////DELETE
  const handleDelete = (e) => {
    e.preventDefault();
    if (submissionid) {
      deleteFormSubmission(
        data,
        formsyDetails,
        comments,
        signatures,
        navigate,
        location
      );
    } else {
      alert("The form could not be deleted because it has not been saved");
    }
  };

  ////SHARE
  const toggleShareModal = () => {
    setShowShare(!showShare);
  };

  const handleShareSend = (recipientList, shareComments) => {
    sendShareEmail(
      recipientList,
      shareComments,
      location.pathname,
      data,
      formsyDetails,
      comments,
      signatures,
      formsyConfig.FormDescription
    );
  };

  ////COMMENTS FUNCTIONALITY
  const [commentToEdit, setCommentToEdit] = useState();

  const toggleCommentModal = (editComment) => {
    if (!showComment && editComment) setCommentToEdit(editComment);
    setShowComment(!showComment);
  };

  const handleComments = ({ action, fieldName, comment, acknowledge }) => {
    if (action === "ADD") {
      setShowComment(true);
    } else if (comment !== undefined) {
      if (action === "EDIT") {
        toggleCommentModal(comment);
      } else if (action === "DELETE") {
        handleDeleteComment(comment);
      } else if (action === "ACKNOWLEDGE" && acknowledge !== undefined) {
        handleAcknowledgeComment(comment, acknowledge);
      }
    } else if (action === "GET") {
      return parsedComments.filter(
        (comment) => comment.field._text === fieldName
      );
    }
  };

  const commentFields = () => {
    let commentOptions = [];
    commentOptions.push({ _id: "EntireForm", name: "Entire Form----" });
    for (const property in data) {
      if (
        data[property]["@type"] !== "section" &&
        data[property]["@type"] !== "label" &&
        data[property]["@type"] !== "User" &&
        data[property]["@type"] !== "hidden"
      ) {
        const option = {
          _id: property,
          name:
            data[property]["@label"] !== undefined &&
            data[property]["@label"] !== ""
              ? data[property]["@label"]
              : property,
        };
        commentOptions.push(option);
      }
    }
    return commentOptions;
  };

  const parsedComments = useMemo(() => {
    if (!comments) {
      return [];
    }

    const commentObj = xml2js(`<comments type="array">${comments}</comments>`, {
      compact: true,
      spaces: 4,
    });

    return ArrayCheck(commentObj.comments.comment);
  }, [comments]);

  const onCommentSave = (comment, commentToEdit) => {
    let formsyComments;
    if (commentToEdit) {
      const commentXml = `<comment><createdby>${
        commentToEdit.createdby._text
      }</createdby><datetime>${commentToEdit.datetime._text}</datetime><field>${
        commentToEdit.field._text
      }</field><comment>${commentToEdit.comment._text}</comment>${
        commentToEdit.acknowledgedby
          ? `<acknowledgedby>${commentToEdit.acknowledgedby._text}</acknowledgedby><acknowledgedon>${commentToEdit.acknowledgedon._text}</acknowledgedon>`
          : ""
      }</comment>`;
      formsyComments =
        comments !== undefined
          ? comments.replace(commentXml, comment)
          : comment;
      setComments(formsyComments);
      setCommentToEdit(undefined);
    } else {
      formsyComments = comments !== undefined ? comments + comment : comment;
      setComments(formsyComments);
    }

    setShowComment(false);
    handleSubmit(null, null, formsyComments, null);
  };

  const handleDeleteComment = (comment) => {
    const commentXml = `<comment><createdby>${
      safyInfo.user.displayName
    }</createdby><datetime>${comment.datetime._text}</datetime><field>${
      comment.field._text
    }</field><comment>${comment.comment._text}</comment>${
      comment.acknowledgedby
        ? `<acknowledgedby>${comment.acknowledgedby._text}</acknowledgedby><acknowledgedon>${comment.acknowledgedon._text}</acknowledgedon>`
        : ""
    }</comment>`;

    let formsyComments = comments.replace(commentXml, "");
    if (formsyComments === "") formsyComments = undefined;
    setComments(formsyComments);
    handleSubmit(null, null, formsyComments, null);
  };

  const toggleCommentToEdit = (comment) => {
    setCommentToEdit(comment);
    setShowComment(true);
  };

  const handleAcknowledgeComment = (comment, acknowledge) => {
    if (acknowledge) {
      const oldCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment></comment>`;
      const newCommentXML = `<comment><createdby>${
        comment.createdby._text
      }</createdby><datetime>${comment.datetime._text}</datetime><field>${
        comment.field._text
      }</field><comment>${comment.comment._text}</comment><acknowledgedby>${
        safyInfo.user.displayName
      }</acknowledgedby><acknowledgedon>${moment().format(
        "L"
      )} ${moment().format("LT")}</acknowledgedon></comment>`;
      const formsyComments = comments.replace(oldCommentXML, newCommentXML);
      setComments(formsyComments);
      handleSubmit(null, null, formsyComments, null);
    } else {
      const oldCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment><acknowledgedby>${comment.acknowledgedby._text}</acknowledgedby><acknowledgedon>${comment.acknowledgedon._text}</acknowledgedon></comment>`;
      const newCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment></comment>`;
      const formsyComments = comments.replace(oldCommentXML, newCommentXML);
      setComments(formsyComments);
      handleSubmit(null, null, formsyComments, null);
    }
  };

  const handleCommentNavigation = (fieldName) => {
    const element = document.getElementById(fieldName);
    if (element.type === "checkbox")
      scrollTo(
        document.getElementById(fieldName).parentElement.parentElement
          .parentElement.offsetTop - 50,
        () => {
          document.getElementById(fieldName).focus();
        }
      );
    else
      scrollTo(document.getElementById(fieldName).offsetTop - 50, () => {
        document.getElementById(fieldName).focus();
      });
  };

  const renderComments = () => {
    return parsedComments
      .filter(
        (comment) =>
          comment.field._text === "EntireForm" ||
          comment.field._text === "Entire Form----"
      )
      .map((comment, index) => (
        <div className="mt-2" key={`form-comment-${index}`}>
          {showFormComment && (
            <div
              className={`${
                comment.acknowledgedby ? "bg-safy3-50" : "bg-warning"
              } p-2 m-2 rounded d-print-none`}
              title={
                comment.acknowledgedby
                  ? `Acknowledged by: ${comment.acknowledgedby._text} ${comment.acknowledgedon._text}`
                  : ""
              }
            >
              <span className="h6">
                {comment.createdby._text ===
                  JSON.parse(localStorage.getItem("safyInfo")).user
                    .displayName && (
                  <>
                    <i
                      title="Delete Comment"
                      className="far fa-trash-alt text-white me-2"
                      onClick={() => {
                        confirmAlert({
                          title: "Delete Comment",
                          message:
                            "Are you sure you want to delete this comment?",
                          buttons: [
                            {
                              label: "Yes",
                              onClick: () => {
                                handleDeleteComment(comment);
                              },
                            },
                            {
                              label: "No",
                              onClick: () => {
                                /* do nothing */
                              },
                            },
                          ],
                        });
                      }}
                    ></i>
                    {!comment.acknowledgedby && (
                      <i
                        title="Edit Comment"
                        className="fas fa-pen text-white me-2"
                        onClick={(e) => {
                          toggleCommentToEdit(comment);
                        }}
                      ></i>
                    )}
                  </>
                )}
                {comment.acknowledgedby && (
                  <i
                    className="far fa-check-square text-white me-2"
                    onClick={() => {
                      if (
                        comment.acknowledgedby._text ===
                        JSON.parse(localStorage.getItem("safyInfo")).user
                          .displayName
                      )
                        handleAcknowledgeComment(comment, false);
                    }}
                  ></i>
                )}
                {!comment.acknowledgedby && (
                  <i
                    className="far fa-square text-white me-2"
                    onClick={() => {
                      handleAcknowledgeComment(comment, true);
                    }}
                    title="Acknowledge Comment"
                  ></i>
                )}
                <br />
                {`${comment.createdby._text} - ${comment.datetime._text}`}
              </span>
              <br />
              Regarding:{" "}
              {comment.field._text !== "EntireForm" &&
              comment.field._text !== "Entire Form----" ? (
                <button
                  className="btn btn-link"
                  onClick={() => {
                    setTimeout(() => {
                      handleCommentNavigation(comment.field._text);
                    }, 100);
                  }}
                >
                  {comment.field._text}
                </button>
              ) : (
                <>{comment.field._text}</>
              )}
              <br />
              {comment.comment._text}
            </div>
          )}
        </div>
      ));
  };

  function scrollTo(offset, callback) {
    const fixedOffset = offset.toFixed();
    const onScroll = function () {
      if (window.pageYOffset.toFixed() === fixedOffset) {
        window.removeEventListener("scroll", onScroll);
        callback();
      }
    };

    window.addEventListener("scroll", onScroll);
    onScroll();
    window.scrollTo({
      top: offset,
      behavior: "smooth",
    });
  }

  //SIGNATURE FUNCTIONALITY
  const toggleSignatureModal = (e) => {
    e.preventDefault();
    if (!isSaveInProgress) setShowSignatureModal(true);
  };

  const saveSignature = async (signatureData) => {
    let sigArray = [];
    if (Array.isArray(signatureData)) {
      sigArray = signatureData;
    } else {
      sigArray.push(signatureData);
    }
    setSignatures(sigArray);

    const formsyDetailsObj = { ...formsyDetails };

    if (formsyDetailsObj.FinalSigned !== "1" && checkFinalSigned(sigArray)) {
      formsyDetailsObj.FinalSigned = "1";

      if (
        formsyDetailsObj.FinalSignedDate === "" ||
        formsyDetailsObj.FinalSignedDate === "1900-01-01T00:00:00"
      ) {
        formsyDetailsObj.FinalSignedDate = new Date();
      }

      setFormsyDetails(formsyDetailsObj);
      setComments(undefined);
    }

    handleSubmit(
      null,
      formsyDetailsObj,
      formsyDetailsObj.FinalSigned === "1" ? undefined : null,
      sigArray
    );
  };

  const checkFinalSigned = (sigArray) => {
    if (helperConfiguration.finalSignType === "Any") {
      return true;
    }

    const requiredSignatureTypes = helperConfiguration.finalSignType.split(",");
    const capturedSignatureTypes = sigArray.map((sig) => sig.signatureType);
    const missingSigTypes = requiredSignatureTypes.filter(
      (type) => !capturedSignatureTypes.includes(type)
    );
    return missingSigTypes.length === 0;
  };

  //COMPARE
  const compare = (field) => {
    if (doCompare)
      return compareForms(field, data, allFormSubmissions, formsyDetails);
  };

  const toggleCompare = () => {
    setDoCompare(!doCompare);
  };

  //CUSTOM
  const [expandStatus, setExpandStatus] = useState(true);
  const [showDefinitions, setShowDefinitions] = useState(false);

  const dateOfAssessment = useMemo(() => {
    return getFieldValue(data.DateOfAssessment);
  }, [data]);

  const previousAssessment = useMemo(() => {
    const previous = allFormSubmissions
      .filter((f) => {
        if (f.Active === "0") return false;

        if (f.SubmissionId === formsyDetails.SubmissionId) return false;

        if (!dateOfAssessment) return false;

        const assessmentDate = moment(dateOfAssessment);
        const fDateOfAssessment = moment(
          getFieldValue(f.FormResults.results.DateOfAssessment)
        );
        return fDateOfAssessment.isBefore(assessmentDate);
      })
      .sort((a, b) => {
        const aData = a.FormResults.results;
        const bData = b.FormResults.results;
        const aStart = moment(getFieldValue(aData.DateOfAssessment));
        const bStart = moment(getFieldValue(bData.DateOfAssessment));

        if (aStart.isAfter(bStart)) return 1;
        if (bStart.isAfter(aStart)) return -1;
        return 0;
      })[0];

    return previous?.FormResults.results;
  }, [allFormSubmissions, formsyDetails, dateOfAssessment]);

  const handleExpandStatusChange = (overrideValue, callback) => {
    if (overrideValue !== undefined) {
      setExpandStatus(overrideValue);
      if (callback) {
        window.setTimeout(callback, 5);
      }
    } else setExpandStatus(!expandStatus);
  };

  const QuestionDefinition = (fieldName) => {
    const definition = definitions[fieldName];

    return definition ? (
      <div className="container-fluid bg-light rounded">
        <div className="row mt-2 border-bottom border-dark bg-safy-gray">
          <div className="col py-2 border-dark">
            {getFieldLabel(formConfig[fieldName])}
          </div>
        </div>
        <div className="row border-bottom border-dark">
          <div className="col-3 py-2 border-end border-dark">
            Clear Strength
          </div>
          <div className="col py-2 border-dark">{definition.strength}</div>
        </div>
        <div className="row border-bottom border-dark">
          <div className="col-3 py-2 border-end border-dark">
            Baseline / Adequate
          </div>
          <div className="col py-2 border-dark">{definition.baseline}</div>
        </div>
        <div className="row">
          <div className="col-3 py-2 border-end border-dark">
            Serious Challenge
          </div>
          <div className="col py-2 border-dark">{definition.challenge}</div>
        </div>
      </div>
    ) : null;
  };

  const QuestionIndicator = (fieldName) => {
    const currentAnswer = getFieldValue(data[fieldName]);
    const previousAnswer = getFieldValue(previousAssessment[fieldName]);

    if (isNaN(currentAnswer) || isNaN(previousAnswer)) {
      return <div className="col-1"></div>;
    }

    const cA = parseInt(currentAnswer);
    const pA = parseInt(previousAnswer);

    const icon =
      cA > pA
        ? "fas fa-lg fa-circle-check text-success"
        : cA < pA
        ? "fas fa-lg fa-circle-xmark text-danger"
        : cA === 2
        ? "fas fa-lg fa-circle-check text-success"
        : "fas fa-lg fa-circle-minus text-warning";
    return (
      <div className="col-1 py-2 text-center">
        <i className={icon} />
      </div>
    );
  };

  const Question = (fieldName, fieldDisabled) => {
    const questionOptions = [
      {
        _id: "N/A",
        name: "N/A",
      },
      {
        _id: "2",
        name: "2",
      },
      {
        _id: "1",
        name: "1",
      },
      {
        _id: "0",
        name: "0",
      },
      {
        _id: "-1",
        name: "-1",
      },
      {
        _id: "-2",
        name: "-2",
      },
      {
        _id: "-3",
        name: "-3",
      },
      {
        _id: "UK",
        name: "UK",
      },
    ];

    return (
      <div className="row border-top" key={fieldName}>
        <div className="col-6 py-2 border-end">
          {showDefinitions ? (
            QuestionDefinition(fieldName)
          ) : (
            <div
              data-tooltip-id="definition-tooltip"
              data-tooltip-html={ReactDOMServer.renderToString(
                QuestionDefinition(fieldName)
              )}
            >
              {getFieldLabel(formConfig[fieldName])}
            </div>
          )}
          {errors[fieldName] ? (
            <div className="mt-1 alert alert-danger">{errors[fieldName]}</div>
          ) : null}
        </div>
        {fieldName.includes("Comments") ? (
          <div className="col py-2">
            <FormInput
              name={fieldName}
              value={getFieldValue(data[fieldName])}
              onChange={handleChange}
              commentsHandler={handleComments}
              disabled={formDisabled}
            />
          </div>
        ) : (
          <>
            {previousAssessment ? (
              <div className="col py-2">
                <FormInput
                  name={fieldName}
                  value={getFieldValue(previousAssessment[fieldName])}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={true}
                />
              </div>
            ) : null}
            <div className="col py-2">
              <FormSelect
                name={fieldName}
                options={questionOptions}
                value={getFieldValue(data[fieldName])}
                onChange={handleChange}
                filter="contains"
                autoSelectMatches={true}
                disabled={formDisabled || fieldDisabled}
              />
            </div>
            {previousAssessment ? QuestionIndicator(fieldName) : null}
          </>
        )}
      </div>
    );
  };

  const getCategoryFields = (prefix) => {
    return Object.keys(data).filter((key) => key.startsWith(prefix));
  };

  ////UI
  return (
    <>
      <div className="mb-2">
        <BackIcon />
      </div>
      <div className={!submissionid ? "watermarked" : ""} ref={pdfRef}>
        <ClientFormHeader
          formTitle={formsyConfig.FormDescription}
          clientData={clientData.client}
          formsyDetails={formsyDetails}
        />
        <FormButtonBar
          config={helperConfiguration.buttonBarConfig}
          onSave={handleSubmit}
          onClone={handleOnClone}
          onDelete={handleDelete}
          onSignClicked={toggleSignatureModal}
          onComment={toggleCommentModal}
          onCompare={toggleCompare}
          onShare={toggleShareModal}
          signed={signatures.length > 0}
          finalSigned={formsyDetails.FinalSigned === "1"}
          validate={validate}
          pdfRef={pdfRef}
          filename={`${formsyConfig.FormDescription}`}
          newestVersion={
            formsyConfig.VersionIntId === formsyConfig.LatestVersionIntId
          }
        />
        <div className="py-2 border-bottom border-dark">
          <b>Selected Client Program:</b>{" "}
          {getSelectedProgramName(data, clientData)}
        </div>
        {renderComments()}
        {parsedComments.filter(
          (comment) =>
            comment.field._text === "EntireForm" ||
            comment.field._text === "Entire Form----"
        ).length > 0 && (
          <i
            className="fas fa-comment-alt text-warning float-end mb-2 ms-2"
            onClick={() => setShowFormComment(!showFormComment)}
          ></i>
        )}
        <div className="mt-4">
          {data && Object.keys(data).length > 0 && (
            <>
              <div className="row">
                <div className="col">
                  <FormInput
                    name="DateOfAssessment"
                    label={getFieldLabel(formConfig.DateOfAssessment)}
                    error={errors.DateOfAssessment}
                    value={getFieldValue(data.DateOfAssessment)}
                    onChange={handleChange}
                    identical={compare("DateOfAssessment")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                    type="date"
                  />
                </div>
                <div className="col">
                  <FormRadioButton
                    name="TypeOfAssessment"
                    label={getFieldLabel(formConfig.TypeOfAssessment)}
                    error={errors.TypeOfAssessment}
                    value={getFieldValue(data.TypeOfAssessment)}
                    options={getFieldOptions(data.TypeOfAssessment)}
                    onChange={handleChange}
                    identical={compare("TypeOfAssessment")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
              </div>
              <FormInput
                name="AssessorName"
                label={getFieldLabel(formConfig.AssessorName)}
                error={errors.AssessorName}
                value={getFieldValue(data.AssessorName)}
                onChange={handleChange}
                identical={compare("AssessorName")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <div className="row mb-2">
                <div className="col">
                  <div className="clearfix">
                    <div className="float-start">
                      <button
                        className="btn btn-primary ms-2"
                        onClick={(e) => {
                          handleExpandStatusChange();
                        }}
                      >
                        {expandStatus === true ? "Collapse All" : "Expand All"}
                      </button>
                      <button
                        className="btn btn-primary ms-2"
                        onClick={(e) => {
                          setShowDefinitions(!showDefinitions);
                        }}
                      >
                        {showDefinitions === true
                          ? "Hide Definitions"
                          : "Show Definitions"}
                      </button>
                    </div>
                  </div>
                </div>
                <div className="col">
                  {(getFieldValue(data.TypeOfAssessment) === "Update" ||
                    getFieldValue(data.TypeOfAssessment) === "Closure") &&
                  previousAssessment ? (
                    <div>
                      Previous Assessment Date:
                      <br />
                      {moment(
                        getFieldValue(previousAssessment.DateOfAssessment)
                      ).format("L")}
                    </div>
                  ) : null}
                </div>
              </div>
              <FunctionalAreaExpandDiv
                id="Environment"
                expandStatus={expandStatus}
                className="container bg-secondary p-3 border-bottom"
                title={<span className="text-white">A. Environments</span>}
                preserveChildren={true}
                score={getFieldValue(data.AENVOverall)}
                previousScore={getFieldValue(previousAssessment?.AENVOverall)}
              >
                <div className="container bg-light fw-bold">
                  {getCategoryFields("AENV").map((fieldName) =>
                    Question(fieldName)
                  )}
                </div>
              </FunctionalAreaExpandDiv>
              <FunctionalAreaExpandDiv
                id="Capabilities"
                expandStatus={expandStatus}
                className="container bg-secondary p-3 border-bottom"
                title={
                  <span className="text-white">B. Parental Capabilities</span>
                }
                subTitle={
                  <b>
                    Note: This section refers to biological parent(s), if
                    present, or current caregiver(s).
                  </b>
                }
                preserveChildren={true}
                score={getFieldValue(data.BPCOverall)}
                previousScore={getFieldValue(previousAssessment?.BPCOverall)}
              >
                <div className="container bg-light fw-bold">
                  {getCategoryFields("BPC").map((fieldName) =>
                    Question(fieldName)
                  )}
                </div>
              </FunctionalAreaExpandDiv>
              <FunctionalAreaExpandDiv
                id="Interactions"
                expandStatus={expandStatus}
                className="container bg-secondary p-3 border-bottom"
                title={
                  <span className="text-white">C. Family Interactions</span>
                }
                subTitle={
                  <b>
                    Note: This section refers to family members living in the
                    same or different households.
                  </b>
                }
                preserveChildren={true}
                score={getFieldValue(data.CFIOverall)}
                previousScore={getFieldValue(previousAssessment?.CFIOverall)}
              >
                <div className="container bg-light fw-bold">
                  {getCategoryFields("CFI").map((fieldName) =>
                    Question(fieldName)
                  )}
                </div>
              </FunctionalAreaExpandDiv>
              <FunctionalAreaExpandDiv
                id="Safety"
                expandStatus={expandStatus}
                className="container bg-secondary p-3 border-bottom"
                title={<span className="text-white">D. Family Safety</span>}
                subTitle={
                  <b>
                    Note: This section refers to family members living in the
                    same or different households.
                  </b>
                }
                preserveChildren={true}
                score={getFieldValue(data.DFSOverall)}
                previousScore={getFieldValue(previousAssessment?.DFSOverall)}
              >
                <div className="container bg-light fw-bold">
                  {getCategoryFields("DFS").map((fieldName) =>
                    Question(fieldName)
                  )}
                </div>
              </FunctionalAreaExpandDiv>
              <FunctionalAreaExpandDiv
                id="ChildWellbeing"
                expandStatus={expandStatus}
                className="container bg-secondary p-3 border-bottom"
                title={
                  <span className="text-white">
                    E. Child Well-Being (Age 0-12)
                  </span>
                }
                subTitle={
                  <b>
                    Note: This section pertains to all the children in the
                    family ages 0-12. If more than one child, children may have
                    different issues. Rate the family; thus if any child has,
                    for example, a behavioral issue, the family as a whole
                    experiences that challenge. In this way, all children in the
                    family contribute to the assessment ratings.
                  </b>
                }
                preserveChildren={true}
                score={getFieldValue(data.ECWOverall)}
                previousScore={getFieldValue(previousAssessment?.ECWOverall)}
              >
                <div className="container bg-light fw-bold">
                  {getCategoryFields("ECW").map((fieldName) =>
                    Question(fieldName)
                  )}
                </div>
              </FunctionalAreaExpandDiv>
              <FunctionalAreaExpandDiv
                id="YouthWellbeing"
                expandStatus={expandStatus}
                className="container bg-secondary p-3 border-bottom"
                title={
                  <span className="text-white">
                    F. Youth Well-being (Age 13-21)
                  </span>
                }
                subTitle={
                  <b>
                    Note: This section pertains to all youths in the family. If
                    more than one youth, they may have different issues. Rate
                    the family; thus if any youth has, for example, a behavioral
                    issue, the family as a whole experiences that challenge. In
                    this way, all youths in the family contribute to the
                    assessment ratings.
                  </b>
                }
                preserveChildren={true}
                score={getFieldValue(data.FYWOverall)}
                previousScore={getFieldValue(previousAssessment?.FYWOverall)}
              >
                <div className="container bg-light fw-bold">
                  {getCategoryFields("FYW").map((fieldName) =>
                    Question(fieldName)
                  )}
                </div>
              </FunctionalAreaExpandDiv>
              <FunctionalAreaExpandDiv
                id="Social"
                expandStatus={expandStatus}
                className="container bg-secondary p-3 border-bottom"
                title={
                  <span className="text-white">G. Social/Community Life</span>
                }
                preserveChildren={true}
                score={getFieldValue(data.GSCLOverall)}
                previousScore={getFieldValue(previousAssessment?.GSCLOverall)}
              >
                <div className="container bg-light fw-bold">
                  {getCategoryFields("GSCL").map((fieldName) =>
                    Question(fieldName)
                  )}
                </div>
              </FunctionalAreaExpandDiv>
              <FunctionalAreaExpandDiv
                id="Sufficiency"
                expandStatus={expandStatus}
                className="container bg-secondary p-3 border-bottom"
                title={<span className="text-white">H. Self-Sufficiency</span>}
                preserveChildren={true}
                score={getFieldValue(data.HSSOverall)}
                previousScore={getFieldValue(previousAssessment?.HSSOverall)}
              >
                <div className="container bg-light fw-bold">
                  {getCategoryFields("HSS").map((fieldName) =>
                    Question(fieldName)
                  )}
                </div>
              </FunctionalAreaExpandDiv>
              <FunctionalAreaExpandDiv
                id="Health"
                expandStatus={expandStatus}
                className="container bg-secondary p-3 border-bottom"
                title={<span className="text-white">I. Family Health</span>}
                preserveChildren={true}
                score={getFieldValue(data.IFHOverall)}
                previousScore={getFieldValue(previousAssessment?.IFHOverall)}
              >
                <div className="container bg-light fw-bold">
                  {getCategoryFields("IFH").map((fieldName) =>
                    Question(fieldName)
                  )}
                </div>
              </FunctionalAreaExpandDiv>
              <FunctionalAreaExpandDiv
                id="Ambivalence"
                expandStatus={expandStatus}
                className="container bg-secondary p-3 border-bottom"
                title={
                  <span className="text-white">
                    J. Caregiver/Child Ambivalence
                  </span>
                }
                subTitle={
                  <b>Note: This section is for Reunification cases only.</b>
                }
                preserveChildren={true}
                score={getFieldValue(data.JCCAOverall)}
                previousScore={getFieldValue(previousAssessment?.JCCAOverall)}
              >
                <div className="container bg-light fw-bold">
                  {getCategoryFields("JCCA").map((fieldName) =>
                    Question(fieldName)
                  )}
                </div>
              </FunctionalAreaExpandDiv>
              <FunctionalAreaExpandDiv
                id="Readiness"
                expandStatus={expandStatus}
                className="container bg-secondary p-3 border-bottom"
                title={
                  <span className="text-white">
                    K. Readiness for Reunification
                  </span>
                }
                subTitle={
                  <b>Note: This section is for Reunification cases only.</b>
                }
                preserveChildren={true}
                score={getFieldValue(data.KRROverall)}
                previousScore={getFieldValue(previousAssessment?.KRROverall)}
              >
                <div className="container bg-light fw-bold">
                  <FormCheckbox
                    name="KRRNA"
                    label={getFieldLabel(formConfig.KRRNA)}
                    checked={getFieldValue(data.KRRNA) === "true"}
                    onChange={handleChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                    divClassName="ms-3 mb-3"
                  />
                  {getFieldValue(data.KRRNA) !== "true"
                    ? getCategoryFields("KRR")
                        .filter((field) => !field.endsWith("NA"))
                        .map((fieldName) => Question(fieldName))
                    : null}
                </div>
              </FunctionalAreaExpandDiv>
              <FunctionalAreaExpandDiv
                id="Trauma"
                expandStatus={expandStatus}
                className="container bg-secondary p-3 border-bottom"
                title={
                  <>
                    <span className="text-white">L. Trauma</span>
                    <FormCheckbox
                      name="LTNA"
                      label={getFieldLabel(formConfig.LTNA)}
                      checked={getFieldValue(data.LTNA) === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-5 text-white"
                    />
                  </>
                }
                preserveChildren={true}
                score={getFieldValue(data.LTOverall)}
                previousScore={getFieldValue(previousAssessment?.LTOverall)}
              >
                <div className="container bg-light fw-bold">
                  {getFieldValue(data.LTNA) !== "true"
                    ? getCategoryFields("LT")
                        .filter((field) => !field.endsWith("NA"))
                        .map((fieldName) =>
                          Question(
                            fieldName,
                            getFieldValue(data.TypeOfAssessment) !== "Intake"
                          )
                        )
                    : null}
                </div>
              </FunctionalAreaExpandDiv>
              <FunctionalAreaExpandDiv
                id="PostTrauma"
                expandStatus={expandStatus}
                className="container bg-secondary p-3 border-bottom"
                title={
                  <>
                    <span className="text-white">
                      M. Post-Trauma Well-Being
                    </span>
                    <FormCheckbox
                      name="MPTNA"
                      label={getFieldLabel(formConfig.MPTNA)}
                      checked={getFieldValue(data.MPTNA) === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-5 text-white"
                    />
                  </>
                }
                preserveChildren={true}
                score={getFieldValue(data.MPTOverall)}
                previousScore={getFieldValue(previousAssessment?.MPTOverall)}
              >
                <div className="container bg-light fw-bold">
                  {getFieldValue(data.MPTNA) !== "true"
                    ? getCategoryFields("MPT")
                        .filter((field) => !field.endsWith("NA"))
                        .map((fieldName) =>
                          Question(
                            fieldName,
                            getFieldValue(data.TypeOfAssessment) === "Intake" ||
                              !getCategoryFields("LT").find(
                                // if any LT field has a negative value, then show the MPT fields
                                (fieldName) =>
                                  !fieldName.includes("Comments") &&
                                  !fieldName.endsWith("NA") &&
                                  getFieldValue(data[fieldName])?.includes("-")
                              )
                          )
                        )
                    : null}
                </div>
              </FunctionalAreaExpandDiv>
              <Tooltip
                className="w-50 border-dark text-black fw-bold"
                id="definition-tooltip"
              ></Tooltip>
            </>
          )}
          <div>
            <div
              className="btn btn-primary m-2 d-print-none"
              onClick={() => {
                handleSubmit(null, null, null, null);
              }}
            >
              Save
            </div>
            <Signature
              canvasWidth="450"
              canvasHeight="200"
              modalTitle="Report Signatures"
              careTeams={careTeams}
              signatures={signatures}
              onSign={saveSignature}
              clientData={clientData.clint}
              showModal={showSignatureModal}
              onHide={() => {
                setShowSignatureModal(false);
              }}
              finalSignType={helperConfiguration.finalSignType}
              initialSignatureType={helperConfiguration.signatureType}
            />
            <Comment
              showModal={showComment}
              commentToEdit={commentToEdit}
              onHide={() => {
                setShowComment(false);
                setCommentToEdit(undefined);
              }}
              onSave={onCommentSave}
              fields={commentFields()}
            />
            {showShare ? (
              <ShareModal
                showShare={showShare}
                onHide={() => setShowShare(false)}
                onSend={handleShareSend}
                submissionId={submissionid}
              />
            ) : null}
          </div>
        </div>
      </div>
    </>
  );
}

export default SAFYNCFAS;
